/**
 * TASK-VIZ-018: Combat Visualization Manager Core Implementation
 *
 * Main orchestrator for combat visual effects.
 * Manages all renderers and delegates based on state changes.
 */

import * as PIXI from 'pixi.js';
import { StateDiffDetector } from './state-diff-detector';
import { HealthBarRenderer, HealthBar } from './health-bar-renderer';
import { HealthBarEffectsManager } from './health-bar-effects-manager';
import { DamageNumberRenderer, DamageType, DamageNumber } from './damage-number-renderer';
import { DamageNumberPool } from './damage-number-pool';
import { ProjectileRenderer, VisualProjectile, TimedProjectile, SpriteTimedProjectile } from './projectile-renderer';
import { BuffIconRenderer, BuffIcon, BuffType } from './buff-icon-renderer';
import { CooldownIndicatorRenderer, AbilityCooldownState } from './cooldown-indicator-renderer';
import { preloadAbilityIcons } from './ability-icon-loader';
import { FRONTEND_FEATURE_FLAGS } from '../config/feature-flags';
import { PositionInterpolator } from './position-interpolator';
import { UnitLifecycleTracker } from './unit-lifecycle-tracker';
import { UnitAnimationStateMachine, AnimationState } from './unit-animation-state-machine';
import { DeathSequenceManager, DeathPhase } from './death-sequence-manager';
import { SmokeEffectPool } from './smoke-effect-pool';
import { HitStopManager } from './hit-stop-manager';
import { ScreenShakeManager } from './screen-shake-manager';
import { ImpactParticlePool } from './impact-particle-pool';
import { ImpactParticleRenderer } from './impact-particle-renderer';
import { EffectLayerManager, getBlendModeForDamageType, getScaleForDamageType } from './effects';
import { ElementalDeathEffectRenderer } from './effects/elemental-death-effect-renderer';
import { HazardZoneRenderer } from './hazard-zone-renderer';
import {
  initializeAbilityEffectFactory,
  getAbilityEffectFactory,
  type AbilityEffectRequest
} from './effects/ability-effect-factory.js';
import { getCombatAudioController, type CombatAudioController } from './sound';
import { CombatEventHandler } from './combat-event-handler';
import { getCombatLogService } from './combat-log-service';
import { MovementEffectRenderer } from './effects/movement-effect-renderer';
import { AbilityPopupManager } from './ability-popup-manager';
import { getLibraryEffectService, type LibraryEffectService } from './library-effect-service';
import { loadEffectSprites } from './effects/effect-sprite-renderer';
import type { CombatState, CombatResult, Projectile, DamageDealtEvent, AttackStartedEvent, AxialCoordinate, DeploymentCreature, VisualCombatAttribute, ProjectileLaunchedEvent, CombatEvent, HealingDoneEventData, CleanseAppliedEventData } from '@drawn-of-war/shared';
import { degreesToHexDirection } from '@drawn-of-war/shared';
import { isDamageDealtEvent, isAttackStartedEvent, isProjectileLaunchedEvent, isCleanseAppliedEvent, isShieldAppliedEvent, isReflectTriggeredEvent, isStrikeTelegraphEvent, isStrikeImpactEvent, isUnitWaitingEvent } from '@drawn-of-war/shared';
import { CombatJitterBuffer, DEFAULT_JITTER_BUFFER_CONFIG } from '@drawn-of-war/shared';
import type { UnitWaitingEventData } from '@drawn-of-war/shared';
import type { ShieldAppliedEventData, ReflectTriggeredEventData, StrikeTelegraphEventData, StrikeImpactEventData } from '@drawn-of-war/shared';
import type { CombatGridRenderer } from '../components/CombatGrid/CombatGridRenderer';
import { calculateCreatureSizeMultiplier } from '../components/CombatGrid/CombatGridRenderer';
import { interpolateHexPosition, calculateAlpha, isTeleport } from '../utils/interpolation';
import { type SubHexOffset } from '../utils/sub-hex-positioning.js';
import { ActionCorrelationService, type ReadyDamage } from './action-correlation-service';
import { DamageDisplayQueue } from './damage-display-queue';

/**
 * Data payload for effectFrames event (TASK-EFD-003)
 */
export interface EffectFramesData {
  matchId: string;
  frames: Record<string, VisualCombatAttribute[]>;
}

/**
 * Socket client interface (minimal required methods)
 */
export interface CombatSocketClient {
  onCombatState(callback: (state: CombatState) => void): void;
  onCombatCompleted(callback: (result: CombatResult) => void): void;
  onError(callback: (error: Error) => void): void;
  onDisconnect?(callback: (reason: string) => void): void; // Optional: listen for socket disconnection
  onEvent?(callback: (event: any) => void): void; // Optional: listen for individual combat events (L2-CLAUDE-ATTRS)
  onCombatEvents?(callback: (events: CombatEvent[]) => void): void; // FIX: Listen for batched combat:events (damage_dealt, etc.)
  signalReady(): void; // TASK-TIMING-004: Signal to backend that client is ready
  readonly isConnected?: boolean; // Optional: check if socket is connected
  // TASK-EFD-003: EffectFrames reliable delivery
  onEffectFrames?(callback: (data: EffectFramesData) => void): void;
  requestEffectFrames?(): void; // TASK-EFD-004: Request missing effectFrames
}

/**
 * Effect containers for layering
 */
export interface EffectContainers {
  projectiles: PIXI.Container;
  healthBars: PIXI.Container;
  buffIcons: PIXI.Container;
  damageNumbers: PIXI.Container;
  smokeEffects: PIXI.Container; // TASK-DEATH-004: Death smoke effects
}

/**
 * CombatVisualizationManager
 *
 * Orchestrates all combat visual effects in response to state changes.
 * Manages PixiJS containers, render loop, and event delegation.
 */
export class CombatVisualizationManager {
  private socketClient: CombatSocketClient;
  private stage: PIXI.Container;
  private app: PIXI.Application; // TASK-BUGFIX-RENDER-002: Store app reference for render calls
  private gridRenderer: CombatGridRenderer;
  private stateDiffDetector: StateDiffDetector;

  private currentState: CombatState | null = null;
  private previousState: CombatState | null = null;
  private lastStateUpdateTime: number = 0;
  private effectContainers: EffectContainers;

  // Position interpolation (TASK-RENDER-006)
  private positionInterpolator: PositionInterpolator;
  private readonly STATE_UPDATE_INTERVAL = 100; // 100ms = 10 Hz (server tick rate)

  // State buffer for smooth movement (TASK-SMOOTH-001)
  // Implements client-side entity interpolation following industry best practices
  // Reference: Gabriel Gambetta - Entity Interpolation
  // https://www.gabrielgambetta.com/entity-interpolation.html
  private stateBuffer: Array<{ timestamp: number; state: CombatState }> = [];
  private readonly MAX_BUFFER_SIZE = 3;
  private readonly MAX_BUFFER_AGE_MS = 300;
  private readonly INTERPOLATION_DELAY_MS = 100; // Render 100ms in the past for smooth interpolation
  private currentInterpolatedState: CombatState | null = null; // TASK-SMOOTH-INTEGRATION: Store current frame's interpolated state

  // Sprite lifecycle management (TASK-RENDER-007)
  private unitLifecycleTracker: UnitLifecycleTracker;
  private activeSpriteUnits: Set<string> = new Set(); // Track which units have sprites

  // Animation state management (TASK-COMBAT-VIZ-007)
  private animationStateMachine: UnitAnimationStateMachine;

  // Death sequence management (TASK-DEATH-004)
  private deathSequenceManager: DeathSequenceManager;
  private smokeEffectPool: SmokeEffectPool | null = null;
  private dyingUnits: Set<string> = new Set(); // Track units in death sequence
  private deathElements: Map<string, string> = new Map(); // L3-ELEMENT-DEATH: Store element for each dying unit
  private elementalDeathRenderer: ElementalDeathEffectRenderer | null = null; // L3-ELEMENT-DEATH
  private hazardZoneRenderer: HazardZoneRenderer | null = null; // L3-LINGERING-HAZARDS

  private isRunning: boolean = false;
  private lastFrameTime: number = 0;
  private renderLoopId: number | null = null;
  private renderCallCount: number = 0; // DEBUG: Track render calls

  private isDestroyed: boolean = false;
  private combatEndTimerId: ReturnType<typeof setTimeout> | null = null;

  // Socket connection state tracking (FIX: Animation loop after disconnect)
  private isSocketConnected: boolean = true;

  // Health bar integration (TASK-VIZ-019)
  private healthBarRenderer: HealthBarRenderer;
  private healthBarEffectsManager: HealthBarEffectsManager;
  private healthBars: Map<string, HealthBar> = new Map();
  private unitsInCombat: Map<string, number> = new Map(); // unitId -> last combat timestamp
  private readonly COMBAT_TIMEOUT = 3000; // 3 seconds
  private readonly HEALTH_BAR_OFFSET_Y = 12; // pixels above sprite (reduced for WC2-style smaller sprites)
  private readonly MAX_DELTA_TIME = 0.1; // Maximum delta time (100ms) to prevent huge jumps from tab backgrounding
  private timeoutCheckInterval: number | null = null;

  // Damage number integration (TASK-VIZ-020)
  private damageNumberRenderer: DamageNumberRenderer;
  private damageNumberPool: DamageNumberPool;
  private activeDamageNumbers: DamageNumber[] = [];

  // Projectile integration (TASK-VIZ-021)
  private projectileRenderer: ProjectileRenderer;
  private activeProjectiles: Map<string, VisualProjectile> = new Map();
  // L4-RANGED-004: Event-driven timed projectiles for ranged attacks
  private activeTimedProjectiles: Map<string, SpriteTimedProjectile> = new Map();

  // Buff icon integration (TASK-VIZ-022)
  private buffIconRenderer: BuffIconRenderer;
  private activeBuffIcons: Map<string, BuffIcon> = new Map(); // buffId/debuffId -> icon
  private readonly BUFF_ICON_OFFSET_Y = 35; // pixels above sprite
  private readonly BUFF_ICON_SPACING = 24; // horizontal spacing between icons

  // Cooldown indicator integration (L2-CLAUDE-ATTRS)
  private cooldownIndicatorRenderer: CooldownIndicatorRenderer | null = null;

  // Combat juice managers (L4-JUICE-007)
  private hitStopManager: HitStopManager;
  private screenShakeManager: ScreenShakeManager;
  private impactParticlePool: ImpactParticlePool;
  private impactParticleRenderer: ImpactParticleRenderer;

  // Screen flash overlay (L3-SCREEN-EFFECTS)
  private flashOverlay: PIXI.Graphics | null = null;
  private flashAnimationId: number | null = null;

  // TASK-VIS-002: Hitstop manager for global state pausing

  // Creature sprite data map (creatureId -> DeploymentCreature)
  private creatureDataMap: Map<string, DeploymentCreature> = new Map();

  // Effect layer for combat attribute effects (TASK-ATTR-015)
  private effectLayer: EffectLayerManager | null = null;

  // TASK-VIS-003: Ability popup manager for floating ability names
  private abilityPopupManager: AbilityPopupManager | null = null;

  // Visual attributes map (unitId -> visualAttributes) (TASK-ATTR-016)
  private unitVisualAttributes: Map<string, VisualCombatAttribute[]> = new Map();

  // Track which units have had their effect frames preloaded (PixiJS v8 requirement)
  private preloadedEffectFrames: Set<string> = new Set();

  // TASK-EFD-003/004: EffectFrames reliable delivery tracking
  private pendingEffectFramesRequest: boolean = false;
  private currentMatchId: string | null = null;

  // Combat Audio Controller (TASK-CAC-009)
  private audioController: CombatAudioController;

  // Movement ability rendering (L3-MOVEMENT-ABILITIES)
  private combatEventHandler: CombatEventHandler;
  private movementEffectRenderer: MovementEffectRenderer | null = null;

  // Library effect service for combat effect animations (L4-BATCH1)
  private libraryEffectService: LibraryEffectService;

  // Action correlation system for synchronized damage display (Phase 1 Foundation)
  // Feature toggle defaults to OFF for safe rollout
  private actionCorrelationService: ActionCorrelationService;
  private damageDisplayQueue: DamageDisplayQueue;

  // Jitter buffer for smooth event delivery (prevents event bursts/starvation)
  private jitterBuffer: CombatJitterBuffer<CombatEvent>;

  constructor(
    socketClient: CombatSocketClient,
    gridRenderer: CombatGridRenderer
  ) {
    this.socketClient = socketClient;
    this.gridRenderer = gridRenderer;

    // TASK-BUGFIX-RENDER-002: Store app reference for explicit render calls
    this.app = gridRenderer.getApp();
    console.log('[CombatVisualizationManager] Constructor - app:', this.app);

    console.log('[CombatVisualizationManager] Constructor - gridRenderer:', gridRenderer);
    console.log('[CombatVisualizationManager] Constructor - typeof gridRenderer.getStage:', typeof gridRenderer.getStage);

    const stage = gridRenderer.getStage();
    console.log('[CombatVisualizationManager] Constructor - stage:', stage);
    console.log('[CombatVisualizationManager] Constructor - stage type:', typeof stage);
    console.log('[CombatVisualizationManager] Constructor - stage.addChild:', typeof stage?.addChild);

    this.stage = stage;
    this.stateDiffDetector = new StateDiffDetector();

    // Initialize renderers
    this.healthBarRenderer = new HealthBarRenderer();
    this.healthBarEffectsManager = new HealthBarEffectsManager();
    this.damageNumberRenderer = new DamageNumberRenderer();
    this.damageNumberPool = new DamageNumberPool(this.damageNumberRenderer);
    this.projectileRenderer = new ProjectileRenderer();
    this.buffIconRenderer = new BuffIconRenderer();

    // Initialize combat juice managers (L4-JUICE-007)
    this.hitStopManager = new HitStopManager(gridRenderer);
    this.screenShakeManager = new ScreenShakeManager(this.stage);
    this.impactParticleRenderer = new ImpactParticleRenderer();
    this.impactParticlePool = new ImpactParticlePool(this.impactParticleRenderer);
    console.log('[CombatVisualizationManager] Combat juice managers initialized');

    // Initialize screen flash overlay (L3-SCREEN-EFFECTS)
    this.flashOverlay = new PIXI.Graphics();
    this.stage.addChild(this.flashOverlay);
    console.log('[CombatVisualizationManager] Screen flash overlay initialized');

    // Initialize combat audio controller (TASK-CAC-009)
    this.audioController = getCombatAudioController();
    console.log('[CombatVisualizationManager] Audio controller initialized');

    // Initialize combat event handler and movement effect renderer (L3-MOVEMENT-ABILITIES)
    this.combatEventHandler = new CombatEventHandler();
    this.movementEffectRenderer = new MovementEffectRenderer(this.gridRenderer);
    this.setupMovementEventListeners();
    console.log('[CombatVisualizationManager] Movement effect renderer initialized');

    // Initialize position interpolator (TASK-RENDER-006)
    this.positionInterpolator = new PositionInterpolator();

    // Initialize unit lifecycle tracker (TASK-RENDER-007)
    this.unitLifecycleTracker = new UnitLifecycleTracker();
    this.setupLifecycleEventListeners();

    // Initialize animation state machine (TASK-COMBAT-VIZ-007)
    this.animationStateMachine = new UnitAnimationStateMachine();
    this.setupAnimationStateListeners();

    // Initialize action correlation system (Phase 1 Foundation)
    // Feature toggle defaults to OFF - existing behavior preserved
    this.actionCorrelationService = new ActionCorrelationService({
      enabled: true // Phase 2: Enabled for melee attacks only
    });
    this.damageDisplayQueue = new DamageDisplayQueue();
    this.jitterBuffer = new CombatJitterBuffer<CombatEvent>({
      ...DEFAULT_JITTER_BUFFER_CONFIG,
      tickRateHz: 22,
    });
    console.log('[CombatVisualizationManager] Action correlation services initialized (Phase 2: melee only)');

    // Initialize death sequence manager (TASK-DEATH-004)
    this.deathSequenceManager = new DeathSequenceManager({
      onDeathAnimationStart: (unitId, position) => {
        console.log(`[CombatVisualizationManager] Death animation started for ${unitId}`);
        this.dyingUnits.add(unitId);
        // Transition to death animation state
        this.animationStateMachine.transitionToDeath(unitId);

        // TASK-VIS-004: Log death to combat log
        const unit = this.currentState?.units.find(u => u.unitId === unitId);
        const unitName = this.getCreatureName(unit);
        getCombatLogService().logDeath(unitName);
      },
      onSmokeEffectStart: (unitId, position, element) => {
        console.log(`[CombatVisualizationManager] Death effect started for ${unitId} (element: ${element})`);

        // L3-ELEMENT-DEATH: Use elemental death effect renderer for non-physical elements
        // Convert hex position to pixel position for the effect
        const pixelPos = this.hexToPixelDirect(position);

        if (this.elementalDeathRenderer && element && element !== 'physical') {
          // Use element-specific death effect
          this.elementalDeathRenderer.createDeathEffect(pixelPos, element);
        } else if (this.smokeEffectPool) {
          // Fallback to standard smoke for physical deaths
          this.smokeEffectPool.spawn(position);
        }

        // Start fading the sprite
        const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
        if (spriteData?.container) {
          // Begin fade animation
          spriteData.container.alpha = 0.5;
        }

        // L3-ELEMENT-DEATH: Clean up stored element
        this.deathElements.delete(unitId);
      },
      onDeathComplete: (unitId) => {
        console.log(`[CombatVisualizationManager] Death complete for ${unitId}`);
        this.dyingUnits.delete(unitId);
        // Remove sprite from grid
        if (this.gridRenderer.removeCreatureById) {
          this.gridRenderer.removeCreatureById(unitId);
        }
        // Remove from active sprites
        this.activeSpriteUnits.delete(unitId);
        // Remove health bar if exists
        this.removeHealthBar(unitId);
        // L2-CLAUDE-ATTRS: Remove cooldown indicators
        if (this.cooldownIndicatorRenderer) {
          this.cooldownIndicatorRenderer.removeUnit(unitId);
        }
        // Unregister from animation state machine
        this.animationStateMachine.unregisterUnit(unitId);
      }
    });
    console.log('[CombatVisualizationManager] Death sequence manager initialized');

    // Create effect container layers
    this.effectContainers = {
      projectiles: new PIXI.Container(),
      healthBars: new PIXI.Container(),
      buffIcons: new PIXI.Container(),
      damageNumbers: new PIXI.Container(),
      smokeEffects: new PIXI.Container() // TASK-DEATH-004
    };

    // Add containers to worldContainer (not stage!) so they follow camera transforms (zoom/pan)
    // This ensures health bars, damage numbers, etc. stay aligned with creatures during zoom
    // (bottom to top: projectiles, health bars, buff icons, smoke effects, damage numbers)
    const worldContainer = gridRenderer.getWorldContainer();
    worldContainer.addChild(this.effectContainers.projectiles);
    worldContainer.addChild(this.effectContainers.healthBars);
    worldContainer.addChild(this.effectContainers.buffIcons);
    worldContainer.addChild(this.effectContainers.smokeEffects); // TASK-DEATH-004: Above buffs, below damage numbers
    worldContainer.addChild(this.effectContainers.damageNumbers);

    // TASK-DEATH-004: Initialize smoke effect pool
    this.smokeEffectPool = new SmokeEffectPool(
      this.effectContainers.smokeEffects,
      (coord) => this.hexToPixelDirect(coord)
    );
    console.log('[CombatVisualizationManager] Smoke effect pool initialized');

    // L3-ELEMENT-DEATH: Initialize elemental death effect renderer
    this.elementalDeathRenderer = new ElementalDeathEffectRenderer(this.effectContainers.smokeEffects);
    console.log('[CombatVisualizationManager] Elemental death effect renderer initialized');

    // L3-LINGERING-HAZARDS: Initialize hazard zone renderer
    this.hazardZoneRenderer = new HazardZoneRenderer(this.effectContainers.smokeEffects);
    console.log('[CombatVisualizationManager] Hazard zone renderer initialized');

    // TASK-ATTR-015: Initialize effect layer manager (above damage numbers)
    // Add to worldContainer so effects follow camera transforms (zoom/pan)
    this.effectLayer = new EffectLayerManager(worldContainer);
    this.effectLayer.initialize();
    console.log('[CombatVisualizationManager] Effect layer manager initialized');

    // L4-BATCH1: Initialize library effect service and preload combat effects
    this.libraryEffectService = getLibraryEffectService();
    this.libraryEffectService.preloadCombatEffects().catch(err => {
      console.warn('[CombatVisualizationManager] Failed to preload combat effects:', err);
    });
    console.log('[CombatVisualizationManager] Library effect service initialized');

    // L4-ABILITY-ICONS: Preload ability icons for cooldown indicators
    preloadAbilityIcons().catch(err => {
      console.warn('[CombatVisualizationManager] Failed to preload ability icons:', err);
    });
    console.log('[CombatVisualizationManager] Ability icon preloading started');

    // TASK-VIS-003: Initialize ability popup manager (above effects)
    // Add to worldContainer so popups follow camera transforms (zoom/pan)
    this.abilityPopupManager = new AbilityPopupManager(worldContainer);
    console.log('[CombatVisualizationManager] Ability popup manager initialized');

    // L2-CLAUDE-ATTRS: Initialize cooldown indicator renderer (above buff icons)
    // Add to worldContainer so indicators follow camera transforms (zoom/pan)
    // L4-TAP-005: Skip when Team Ability Panel is active (cooldowns shown in external React UI)
    if (!FRONTEND_FEATURE_FLAGS.USE_TEAM_ABILITY_PANEL) {
      this.cooldownIndicatorRenderer = new CooldownIndicatorRenderer(worldContainer);
      console.log('[CombatVisualizationManager] Cooldown indicator renderer initialized');
    } else {
      console.log('[CombatVisualizationManager] In-world cooldown indicators disabled (Team Ability Panel active)');
    }

    // NOTE: Socket subscriptions moved to start() method to implement initialization-before-subscription pattern
    // This prevents race condition where states arrive during texture preloading and get dropped

    // Start timeout check interval (every 100ms)
    this.timeoutCheckInterval = setInterval(() => this.checkHealthBarTimeouts(), 100) as unknown as number;
  }

  /**
   * Start the visualization manager with 60 FPS render loop
   * Preloads all creature textures before starting to prevent async delays
   *
   * ARCHITECTURE: Implements initialization-before-subscription pattern
   * - Step 1: Preload all textures (async initialization)
   * - Step 2: Subscribe to socket events (only when ready)
   * - Step 3: Start render loop
   * This prevents race condition where states arrive during initialization and get dropped
   */
  public async start(): Promise<void> {
    console.log('[CombatVisualizationManager] start() called - isRunning:', this.isRunning, 'isDestroyed:', this.isDestroyed);

    if (this.isRunning || this.isDestroyed) {
      console.warn('[CombatVisualizationManager] Cannot start - already running or destroyed');
      return;
    }

    // STEP 1: Preload all creature textures before starting combat
    // TASK-TIMING-004: Optimize Initial Render (No Delay)
    console.log('[CombatVisualizationManager] [1/4] Preloading textures...');
    const preloadStart = performance.now();
    await this.preloadAllCreatureTextures();
    console.log(`[CombatVisualizationManager] [1/4] ✓ Textures loaded in ${performance.now() - preloadStart}ms`);

    // STEP 1.6: Initialize ability effect factory for dynamic ability visuals (L2-CLAUDE-ATTRS)
    console.log('[CombatVisualizationManager] [1.6/4] Initializing ability effect factory...');
    const worldContainer = this.gridRenderer.getWorldContainer();
    initializeAbilityEffectFactory(worldContainer);
    console.log('[CombatVisualizationManager] [1.6/4] ✓ Ability effect factory initialized');

    // STEP 2: Subscribe to socket events (now that we're ready to process them)
    console.log('[CombatVisualizationManager] [2/4] Subscribing to socket events...');
    this.socketClient.onCombatState(this.handleStateUpdate.bind(this));
    this.socketClient.onCombatCompleted(this.handleCombatCompleted.bind(this));
    this.socketClient.onError(this.handleError.bind(this));
    // FIX: Register for disconnect events to prevent animation loop after socket disconnect
    if (this.socketClient.onDisconnect) {
      this.socketClient.onDisconnect(this.handleSocketDisconnect.bind(this));
    }
    // L2-CLAUDE-ATTRS: Subscribe to individual combat events for ability visuals
    if (this.socketClient.onEvent) {
      this.socketClient.onEvent(this.handleCombatEvent.bind(this));
    }
    // FIX: Subscribe to batched combat:events for damage numbers (damage_dealt events)
    if (this.socketClient.onCombatEvents) {
      this.socketClient.onCombatEvents(this.handleCombatEvents.bind(this));
    }
    // TASK-EFD-003: Subscribe to effectFrames reliable delivery
    if (this.socketClient.onEffectFrames) {
      this.socketClient.onEffectFrames(this.handleEffectFrames.bind(this));
    }
    console.log('[CombatVisualizationManager] [2/4] ✓ Socket subscriptions active');

    // STEP 3: Signal to backend that we're ready for combat to start
    console.log('[CombatVisualizationManager] [3/4] Signaling ready to backend...');
    this.socketClient.signalReady();
    console.log('[CombatVisualizationManager] [3/4] ✓ Ready signal sent, waiting for combat to start');

    // STEP 4: Start render loop
    console.log('[CombatVisualizationManager] [4/4] Starting render loop...');
    console.log('[CombatVisualizationManager] DEBUG: app:', this.app);
    console.log('[CombatVisualizationManager] DEBUG: app.canvas:', this.app.canvas);
    console.log('[CombatVisualizationManager] DEBUG: app.canvas.parentElement:', this.app.canvas?.parentElement);
    console.log('[CombatVisualizationManager] DEBUG: stage.children.length:', this.stage.children.length);
    this.isRunning = true;
    this.startRenderLoop();
    console.log('[CombatVisualizationManager] [4/4] ✓ Visualization manager fully started and ready');
  }

  /**
   * Stop the render loop
   */
  public stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;

    this.stopRenderLoop();

    // Stop timeout check interval
    if (this.timeoutCheckInterval !== null) {
      clearInterval(this.timeoutCheckInterval);
      this.timeoutCheckInterval = null;
    }
  }

  /**
   * Set state (for reconnection)
   * TASK-SMOOTH-001: Modified to add state to buffer for smooth interpolation
   */
  public setState(state: CombatState): void {
    if (this.isDestroyed) {
      return;
    }

    // TASK-SMOOTH-001: Add to interpolation buffer
    this.addStateToBuffer(state);

    this.currentState = state;

    // Reset diff detector with new state
    this.stateDiffDetector = new StateDiffDetector();
    this.stateDiffDetector.detectChanges(state);
  }

  /**
   * Get current state
   */
  public getCurrentState(): CombatState | null {
    return this.currentState;
  }

  /**
   * TASK-SMOOTH-001: Add state to buffer with timestamp
   * Implements client-side entity interpolation for smooth movement
   * Automatically removes old states (FIFO + age-based cleanup)
   */
  private addStateToBuffer(state: CombatState): void {
    const now = performance.now();

    // Add new state with timestamp
    // Deep-copy units and positions to prevent mutations from corrupting buffered states
    this.stateBuffer.push({
      timestamp: now,
      state: {
        ...state,
        units: state.units.map(u => ({
          ...u,
          position: { ...u.position },
          activeBuffs: u.activeBuffs ? [...u.activeBuffs] : u.activeBuffs,
          activeDebuffs: u.activeDebuffs ? [...u.activeDebuffs] : u.activeDebuffs,
        }))
      }
    });

    // Remove oldest if exceeds max size (FIFO)
    if (this.stateBuffer.length > this.MAX_BUFFER_SIZE) {
      this.stateBuffer.shift();
    }

    // Remove states older than max age
    const cutoffTime = now - this.MAX_BUFFER_AGE_MS;
    this.stateBuffer = this.stateBuffer.filter(s => s.timestamp >= cutoffTime);
  }

  /**
   * TASK-SMOOTH-001: Get buffered states (for testing/debugging)
   * Returns a copy to prevent external modifications
   */
  public getStateBuffer(): Array<{ timestamp: number; state: CombatState }> {
    return [...this.stateBuffer]; // Return copy
  }

  /**
   * TASK-SMOOTH-001: Clear state buffer (for cleanup)
   */
  private clearStateBuffer(): void {
    this.stateBuffer = [];
  }

  /**
   * TASK-SMOOTH-003: Get interpolated combat state for smooth rendering
   *
   * Implements client-side entity interpolation by finding two states that bracket
   * the render time and linearly interpolating between them.
   *
   * @param renderTime - Time to render at (usually now - INTERPOLATION_DELAY_MS)
   * @returns Interpolated state with smooth unit positions, or null if no states available
   */
  private getInterpolatedState(renderTime: number): CombatState | null {
    // Need at least 1 state to render
    if (this.stateBuffer.length === 0) {
      return null;
    }

    // If only 1 state, render it exactly (no interpolation)
    if (this.stateBuffer.length === 1) {
      return this.stateBuffer[0].state;
    }

    // Find two states that bracket the render time
    let newerState = null;
    let olderState = null;

    for (let i = 0; i < this.stateBuffer.length; i++) {
      if (this.stateBuffer[i].timestamp >= renderTime) {
        newerState = this.stateBuffer[i];
        olderState = i > 0 ? this.stateBuffer[i - 1] : null;
        break;
      }
    }

    // If renderTime is after all states, use latest
    if (!newerState) {
      return this.stateBuffer[this.stateBuffer.length - 1].state;
    }

    // If no older state, use newer exactly
    if (!olderState) {
      return newerState.state;
    }

    // Calculate interpolation alpha
    const alpha = calculateAlpha(
      renderTime,
      olderState.timestamp,
      newerState.timestamp
    );

    // Create interpolated state
    const interpolatedState: CombatState = {
      ...newerState.state,
      units: newerState.state.units.map((newUnit, i) => {
        const oldUnit = olderState!.state.units[i];

        // If unit doesn't exist in old state, don't interpolate (new spawn)
        if (!oldUnit) {
          return newUnit;
        }

        // If unit IDs don't match, don't interpolate (different units)
        if (oldUnit.unitId !== newUnit.unitId) {
          return newUnit;
        }

        // TASK-SMOOTH-005: Skip interpolation for dead/dying units
        if (newUnit.status === 'dead' || oldUnit.status === 'dead') {
          return newUnit; // Use exact position for death
        }

        // Detect teleportation (large jumps)
        if (isTeleport(oldUnit.position, newUnit.position)) {
          console.log(`[CombatVisualizationManager] Teleport detected for ${newUnit.unitId} - skipping interpolation`);
          return newUnit;
        }

        // Interpolate position
        const interpolatedPosition = interpolateHexPosition(
          oldUnit.position,
          newUnit.position,
          alpha
        );

        return {
          ...newUnit,
          position: interpolatedPosition
        };
      })
    };

    return interpolatedState;
  }

  /**
   * Set creature sprite data map
   * This should be called before combat starts to provide sprite data for rendering
   * Also preloads creature sounds for the audio controller
   */
  public async setCreatureData(creatures: DeploymentCreature[]): Promise<void> {
    this.creatureDataMap.clear();
    for (const creature of creatures) {
      this.creatureDataMap.set(creature.id, creature);
    }

    // DIAGNOSTIC: Log detailed creature data breakdown
    const player1Creatures = creatures.filter(c => c.playerId === 'player1');
    const player2Creatures = creatures.filter(c => c.playerId === 'player2');
    const withBattlefieldViews = creatures.filter(c => !!c.battlefieldDirectionalViews);

    console.log('[CombatVisualizationManager] Loaded sprite data DIAGNOSTIC:', {
      totalCreatures: creatures.length,
      player1Creatures: player1Creatures.length,
      player2Creatures: player2Creatures.length,
      withBattlefieldViews: withBattlefieldViews.length,
      creatureIds: creatures.map(c => ({ id: c.id.substring(0, 8), player: c.playerId, hasBattlefieldViews: !!c.battlefieldDirectionalViews }))
    });

    // Log warning if player2 creatures are missing
    if (player2Creatures.length === 0) {
      console.error('[CombatVisualizationManager] CRITICAL: No player2 creatures loaded! Enemy sprites will not render.');
    }

    console.log('[CombatVisualizationManager] Loaded sprite data for', creatures.length, 'creatures');

    // TASK-CAC-009: Preload creature sounds for audio controller
    const creaturesWithSounds = creatures.filter((c) => c.sounds);
    if (creaturesWithSounds.length > 0) {
      console.log(`[CombatVisualizationManager] Preloading sounds for ${creaturesWithSounds.length} creatures`);
      await this.audioController.initialize(
        creaturesWithSounds.map((c) => ({
          id: c.id,
          sounds: c.sounds,
        }))
      );
    } else {
      // Still preload shared sounds even if no creature sounds
      await this.audioController.initialize([]);
    }

    // L4-EFFECT-008: Preload Effect Gallery sprites for abilities with effectKeys
    await this.preloadEffectGallerySprites(creatures);
  }

  /**
   * Preload effect sprites from Effect Gallery for abilities with effectKeys
   * L4-EFFECT-008: Collect effectKeys from all creature abilities and fetch from backend
   */
  private async preloadEffectGallerySprites(creatures: DeploymentCreature[]): Promise<void> {
    // Collect all unique effectKeys from creature abilities
    const effectKeys = new Set<string>();

    for (const creature of creatures) {
      // Type assertion for dynamicAbilities which may have effectKeys
      const abilities = (creature as any).dynamicAbilities as Array<{
        effectKeys?: { primary?: string; impact?: string };
      }> | undefined;

      if (!abilities) continue;

      for (const ability of abilities) {
        if (ability.effectKeys?.primary) {
          effectKeys.add(ability.effectKeys.primary);
        }
        if (ability.effectKeys?.impact) {
          effectKeys.add(ability.effectKeys.impact);
        }
      }
    }

    if (effectKeys.size === 0) {
      console.log('[CombatVisualizationManager] No Effect Gallery sprites to preload');
      return;
    }

    console.log(`[CombatVisualizationManager] Preloading ${effectKeys.size} Effect Gallery sprites...`);

    try {
      // Fetch effects from backend API
      const keysParam = Array.from(effectKeys).join(',');
      const response = await fetch(`/api/effects/batch?keys=${encodeURIComponent(keysParam)}`);

      if (!response.ok) {
        console.warn(`[CombatVisualizationManager] Failed to fetch effects: ${response.status}`);
        return;
      }

      const data = await response.json();
      const { effects, found, missing } = data;

      if (missing && missing.length > 0) {
        console.warn(`[CombatVisualizationManager] Missing effects: ${missing.join(', ')}`);
      }

      // Load effects into cache
      await loadEffectSprites(effects);

      console.log(`[CombatVisualizationManager] ✓ Loaded ${found} Effect Gallery sprites`);
    } catch (error) {
      console.error('[CombatVisualizationManager] Error preloading Effect Gallery sprites:', error);
    }
  }

  /**
   * TASK-VIS-004: Get creature name from creature ID
   * Looks up the name from the creatureDataMap using the unit's creatureId
   */
  private getCreatureName(unit: { creatureId: string } | null | undefined): string {
    if (!unit) return 'Unknown';
    const creatureData = this.creatureDataMap.get(unit.creatureId);
    return creatureData?.name || unit.creatureId || 'Unknown';
  }

  /**
   * Preload all creature textures into PixiJS Assets cache
   * This prevents async fallback delays during combat rendering
   */
  private async preloadAllCreatureTextures(): Promise<void> {
    const allFrameUrls = new Set<string>();

    // Collect all frame URLs from creature data
    for (const creature of this.creatureDataMap.values()) {
      if (creature.battlefieldDirectionalViews) {
        // Check all directions (E, NE, SE)
        for (const dirKey of ['E', 'NE', 'SE']) {
          const view = creature.battlefieldDirectionalViews[dirKey as keyof typeof creature.battlefieldDirectionalViews];
          if (view) {
            // Collect all animation frame arrays (including death frames)
            const frameArrays = [
              view.idleFrames || (view as any).idle || [],
              view.walkFrames || (view as any).walk || [],
              view.attackFrames || (view as any).attack || [],
              (view as any).deathFrames || [] // Death animation frames
            ];

            frameArrays.forEach(frames => {
              if (Array.isArray(frames)) {
                frames.forEach(url => {
                  if (url && typeof url === 'string') {
                    allFrameUrls.add(url);
                  }
                });
              }
            });

            // Also add the sprite URL if present
            if (view.sprite && typeof view.sprite === 'string') {
              allFrameUrls.add(view.sprite);
            }
          }
        }
      }
    }

    if (allFrameUrls.size === 0) {
      console.log('[CombatVisualizationManager] No textures to preload');
      return;
    }

    console.log(`[CombatVisualizationManager] Preloading ${allFrameUrls.size} textures...`);

    try {
      // Load all textures in parallel with detailed logging
      const urls = Array.from(allFrameUrls);
      let loadedCount = 0;
      let failedCount = 0;

      await Promise.all(
        urls.map(async (url, index) => {
          try {
            // Normalize URL to handle raw base64 data (challenge creatures have inline sprite data)
            const normalizedUrl = this.normalizeImageUrl(url);
            const isDataUri = normalizedUrl.startsWith('data:');
            const logName = isDataUri ? `base64-${index}` : url.substring(url.lastIndexOf('/') + 1);
            console.log(`[CombatVisualizationManager] Loading texture ${index + 1}/${urls.length}: ${logName}`);

            // Use Texture.from() for data URIs (handles base64 correctly), Assets.load() for HTTP URLs
            if (isDataUri) {
              await PIXI.Texture.from(normalizedUrl);
            } else {
              await PIXI.Assets.load(normalizedUrl);
            }

            loadedCount++;
            console.log(`[CombatVisualizationManager] ✓ Loaded ${loadedCount}/${urls.length}`);
            return true;
          } catch (err) {
            failedCount++;
            console.warn(`[CombatVisualizationManager] ✗ Failed to preload texture (${failedCount} failures): ${url.substring(0, 50)}...`, err);
            return null; // Continue loading other textures even if one fails
          }
        })
      );

      console.log(`[CombatVisualizationManager] ✓ Texture preloading complete: ${loadedCount} loaded, ${failedCount} failed`);
    } catch (error) {
      console.error('[CombatVisualizationManager] Error during texture preloading:', error);
      // Don't throw - combat can still work with async fallback
    }
  }

  /**
   * Normalize image URL to ensure proper format for PixiJS
   * Converts raw base64 data to data URI format (required for challenge creature sprites)
   */
  private normalizeImageUrl(url: string): string {
    // Already a data URI
    if (url.startsWith('data:')) {
      return url;
    }

    // Already a HTTP(S) URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Already a relative path starting with /
    if (url.startsWith('/')) {
      return url;
    }

    // Assume raw base64 data - convert to data URI
    // Detect image format from base64 header
    let mimeType = 'image/png'; // default
    if (url.startsWith('/9j/')) {
      mimeType = 'image/jpeg';
    } else if (url.startsWith('R0lGOD')) {
      mimeType = 'image/gif';
    } else if (url.startsWith('PHN2Zy')) {
      mimeType = 'image/svg+xml';
    }

    console.log('[CombatVisualizationManager] Converting raw base64 to data URI:', {
      detectedType: mimeType,
      base64Preview: url.substring(0, 20)
    });

    return `data:${mimeType};base64,${url}`;
  }

  /**
   * Get effect containers
   */
  public getEffectContainers(): EffectContainers {
    return this.effectContainers;
  }

  /**
   * Get units currently in combat (TASK-VIZ-019)
   */
  public getUnitsInCombat(): Map<string, number> {
    return new Map(this.unitsInCombat);
  }

  /**
   * Get active health bars (TASK-VIZ-019)
   */
  public getHealthBars(): Map<string, HealthBar> {
    return new Map(this.healthBars);
  }

  /**
   * Get active damage numbers (TASK-VIZ-020)
   */
  public getActiveDamageNumbers(): DamageNumber[] {
    return [...this.activeDamageNumbers];
  }

  /**
   * Get damage number pool statistics (TASK-VIZ-020)
   */
  public getDamageNumberPoolStats() {
    return this.damageNumberPool.getStatistics();
  }

  /**
   * Update damage number animations (TASK-VIZ-020)
   */
  public updateDamageNumbers(deltaTime: number): void {
    const currentTime = Date.now();
    const completedIndices: number[] = [];

    for (let i = 0; i < this.activeDamageNumbers.length; i++) {
      const damageNumber = this.activeDamageNumbers[i];

      this.damageNumberRenderer.updateAnimation(damageNumber, currentTime);

      if (damageNumber.isComplete) {
        // Remove from container
        this.effectContainers.damageNumbers.removeChild(damageNumber.container);

        // Return to pool
        this.damageNumberPool.release(damageNumber);

        completedIndices.push(i);
      }
    }

    // Remove completed damage numbers (reverse order to maintain indices)
    for (let i = completedIndices.length - 1; i >= 0; i--) {
      this.activeDamageNumbers.splice(completedIndices[i], 1);
    }
  }

  /**
   * Get active projectiles (TASK-VIZ-021)
   */
  public getActiveProjectiles(): VisualProjectile[] {
    return Array.from(this.activeProjectiles.values());
  }

  /**
   * Get active buff icons (TASK-VIZ-022)
   */
  public getActiveBuffIcons(): BuffIcon[] {
    return Array.from(this.activeBuffIcons.values());
  }

  /**
   * Destroy and cleanup
   */
  public destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.isDestroyed = true;

    // Cancel combat-end linger timeout if still pending
    if (this.combatEndTimerId) {
      clearTimeout(this.combatEndTimerId);
      this.combatEndTimerId = null;
    }

    // Stop render loop
    this.stop();
    this.jitterBuffer.reset();

    // Destroy effect containers (removes from parent automatically)
    // Note: These are in worldContainer, not stage
    this.effectContainers.projectiles.destroy({ children: true });
    this.effectContainers.healthBars.destroy({ children: true });
    this.effectContainers.buffIcons.destroy({ children: true });
    this.effectContainers.damageNumbers.destroy({ children: true });
    this.effectContainers.smokeEffects.destroy({ children: true });

    // TASK-DEATH-004: Cleanup death system
    this.deathSequenceManager.clear();
    this.dyingUnits.clear();
    if (this.smokeEffectPool) {
      this.smokeEffectPool.destroy();
      this.smokeEffectPool = null;
    }
    console.log('[CombatVisualizationManager] Death system cleaned up');

    // Cleanup damage number pool
    this.damageNumberPool.destroy();
    this.activeDamageNumbers = [];

    // Cleanup projectiles
    for (const visualProjectile of this.activeProjectiles.values()) {
      this.projectileRenderer.destroyProjectile(visualProjectile);
    }
    this.activeProjectiles.clear();

    // L4-RANGED-004: Cleanup timed projectiles
    for (const projectile of this.activeTimedProjectiles.values()) {
      this.projectileRenderer.destroyProjectile(projectile);
    }
    this.activeTimedProjectiles.clear();

    // Cleanup buff icons
    for (const buffIcon of this.activeBuffIcons.values()) {
      this.buffIconRenderer.destroyBuffIcon(buffIcon);
    }
    this.activeBuffIcons.clear();

    // Cleanup sprite lifecycle tracker (TASK-RENDER-007)
    this.unitLifecycleTracker.clear();
    this.activeSpriteUnits.clear();

    // Cleanup combat juice managers (L4-JUICE-007)
    this.hitStopManager.destroy();
    this.screenShakeManager.destroy();
    this.impactParticlePool.destroy();
    // TASK-VIS-002: End any active hitstop
    this.hitStopManager.endStatePause();
    console.log('[CombatVisualizationManager] Combat juice managers destroyed');

    // L3-LINGERING-HAZARDS: Cleanup hazard zone renderer
    if (this.hazardZoneRenderer) {
      this.hazardZoneRenderer.clear();
      this.hazardZoneRenderer = null;
      console.log('[CombatVisualizationManager] Hazard zone renderer cleared');
    }

    // TASK-CAC-009: Cleanup combat audio controller
    this.audioController.cleanup();
    console.log('[CombatVisualizationManager] Audio controller cleaned up');

    // TASK-ATTR-015: Cleanup effect layer manager
    if (this.effectLayer) {
      this.effectLayer.dispose();
      this.effectLayer = null;
      console.log('[CombatVisualizationManager] Effect layer manager disposed');
    }

    // TASK-VIS-003: Cleanup ability popup manager
    if (this.abilityPopupManager) {
      this.abilityPopupManager.destroy();
      this.abilityPopupManager = null;
      console.log('[CombatVisualizationManager] Ability popup manager destroyed');
    }

    // TASK-ATTR-016: Clear visual attributes map
    this.unitVisualAttributes.clear();
    this.preloadedEffectFrames.clear();

    // TASK-SMOOTH-001: Clear state buffer
    this.clearStateBuffer();

    this.currentState = null;
  }

  /**
   * Setup lifecycle event listeners (TASK-RENDER-007, TASK-DEATH-004)
   */
  private setupLifecycleEventListeners(): void {
    // Listen for unit spawns
    this.unitLifecycleTracker.onUnitSpawned((event) => {
      // Mark unit as having a sprite
      this.activeSpriteUnits.add(event.unitId);
      console.log('[CombatVisualizationManager] Unit spawned:', event.unitId);
    });

    // Listen for unit despawns
    this.unitLifecycleTracker.onUnitDespawned((event) => {
      // FIX: Don't remove sprite if unit is in death sequence
      // Death sequence manager will handle sprite removal when complete
      // This prevents death animation from being cut short when backend removes dead unit from state
      if (this.deathSequenceManager.isUnitDying(event.unitId)) {
        console.log('[CombatVisualizationManager] Unit despawned but in death sequence, letting death sequence handle cleanup:', event.unitId);
        return;
      }

      // Remove sprite via grid renderer
      if (this.gridRenderer.removeCreatureById) {
        this.gridRenderer.removeCreatureById(event.unitId);
      }

      // Remove from active sprite registry
      this.activeSpriteUnits.delete(event.unitId);
      console.log('[CombatVisualizationManager] Unit despawned:', event.unitId);
    });

    // TASK-DEATH-004: Listen for unit deaths (status change from alive to dead)
    // L3-ELEMENT-DEATH: Pass element for element-specific death effects
    this.unitLifecycleTracker.onUnitDeath((event) => {
      console.log('[CombatVisualizationManager] Unit death detected:', event.unitId);

      // Don't re-trigger if already in death sequence
      if (this.deathSequenceManager.isUnitDying(event.unitId)) {
        console.log('[CombatVisualizationManager] Unit already dying, skipping:', event.unitId);
        return;
      }

      // L3-ELEMENT-DEATH: Get stored element from unit_died event, default to physical
      const element = this.deathElements.get(event.unitId) || 'physical';
      console.log(`[CombatVisualizationManager] Starting death sequence with element: ${element}`);

      // Start death sequence with element
      this.deathSequenceManager.startDeathSequence(event.unitId, event.unit.position, undefined, element);
    });
  }

  /**
   * Setup animation state listeners (TASK-COMBAT-VIZ-007)
   */
  private setupAnimationStateListeners(): void {
    // Listen for animation state transitions
    this.animationStateMachine.onStateChange((transition) => {
      console.log('[CombatVisualizationManager] Animation state transition:', {
        unitId: transition.unitId,
        from: transition.previousState,
        to: transition.newState
      });

      // Tell the renderer to change animation state (with optional direction)
      if (this.gridRenderer.changeAnimationState) {
        this.gridRenderer.changeAnimationState(transition.unitId, transition.newState, transition.direction);
      } else {
        console.warn('[CombatVisualizationManager] Renderer does not support changeAnimationState()');
      }
    });

    // Listen for animation completions from the renderer
    if (this.gridRenderer.onAnimationComplete) {
      this.gridRenderer.onAnimationComplete((event) => {
        console.log('[CombatVisualizationManager] Animation completed:', event);

        // Tell state machine that animation finished
        this.animationStateMachine.onAnimationComplete(event.unitId, event.animationState);
      });
    }

    // Phase 2: Listen for animation impact events to synchronize damage display
    this.animationStateMachine.onAnimationImpact((event) => {
      console.log('[CombatVisualizationManager] Animation impact reached:', {
        unitId: event.unitId.substring(0, 20),
        estimatedImpactTick: event.estimatedImpactTick,
        attackType: event.attackType
      });

      // Notify the correlation service that animation has reached impact frame
      if (this.actionCorrelationService.isEnabled()) {
        this.actionCorrelationService.onAnimationImpact(
          event.unitId,
          event.estimatedImpactTick
        );
      }
    });
  }

  /**
   * Setup movement event listeners (L3-MOVEMENT-ABILITIES)
   * Connects combat event handler to movement effect renderer
   */
  private setupMovementEventListeners(): void {
    this.combatEventHandler.onUnitMoved((data) => {
      console.log('[CombatVisualizationManager] Unit moved event:', data);
      if (this.movementEffectRenderer) {
        this.movementEffectRenderer.playMovementEffect(data);
      }

      // TASK-VIS-004: Log special movement to combat log
      if (data.movementType) {
        const unit = this.currentState?.units.find(u => u.unitId === data.unitId);
        const unitName = this.getCreatureName(unit);
        getCombatLogService().logMovement(unitName, data.movementType);
      }
    });
  }

  /**
   * Handle state update from socket
   *
   * NOTE: This handler is only subscribed AFTER initialization completes (in start() method)
   * so we no longer need to guard against early states arriving during preloading
   */
  private async handleStateUpdate(state: CombatState): Promise<void> {
    if (this.isDestroyed || !state) {
      return;
    }

    // DEBUG: Log visualAttributes presence in received state (first few ticks only)
    if (state.tick <= 3 || state.tick % 60 === 0) {
      const unitsWithVA = state.units.filter(u => u.visualAttributes && u.visualAttributes.length > 0);
      console.log(`[CombatVisualizationManager] handleStateUpdate tick ${state.tick} - received ${state.units.length} units, ${unitsWithVA.length} with visualAttributes`);
    }

    // Note: No isRunning guard needed - we only subscribe when ready (initialization-before-subscription pattern)

    try {
      // Buffer events for steady-rate release in renderFrame
      if (state.events && state.events.length > 0) {
        this.jitterBuffer.enqueue(state.events);
      }

      // TASK-VIS-002: Skip remaining state processing during hitstop (rendering continues)
      if (this.hitStopManager.isInStatePause()) {
        console.log(`[CombatVisualizationManager] State update skipped - in hitstop (${this.hitStopManager.getStatePauseRemaining()}ms remaining)`);
        return;
      }

      // TASK-EFD-003: Track matchId for effectFrames requests
      if (state.matchId && !this.currentMatchId) {
        this.currentMatchId = state.matchId;
        console.log(`[CombatVisualizationManager] Captured matchId: ${state.matchId}`);
      }

      // Track previous state for interpolation (TASK-RENDER-006)
      if (this.currentState) {
        this.previousState = this.currentState;
      }

      // Store current state
      this.currentState = state;

      // TASK-SMOOTH-001: Add to interpolation buffer for smooth 60fps rendering
      // This was missing — the buffer was only populated on reconnection (setState),
      // not during normal gameplay, so interpolation never had data to work with.
      this.addStateToBuffer(state);

      // TASK-VIS-004: Update combat log service with current tick
      getCombatLogService().setCurrentTick(state.tick);

      // Update state timestamp for interpolation (TASK-RENDER-006)
      this.lastStateUpdateTime = Date.now();

      // Update lifecycle tracker for spawn/despawn detection (TASK-RENDER-007)
      this.unitLifecycleTracker.updateState(state);

      // TASK-DEATH-FIX: Fallback death detection for units that may have been missed
      // This catches units where the primary death detection (alive->dead status change) failed
      // due to network timing or other edge cases. We check for:
      // 1. Unit has a sprite rendered (query gridRenderer directly, not activeSpriteUnits)
      // 2. Unit has status === 'dead' in current state
      // 3. Death sequence hasn't started yet
      for (const unit of state.units) {
        if (unit.status === 'dead' && !this.deathSequenceManager.isUnitDying(unit.unitId)) {
          // Check if sprite actually exists in the renderer
          const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);
          if (spriteData) {
            console.log(`[CombatVisualizationManager] Fallback death detection: ${unit.unitId}`);
            this.deathSequenceManager.startDeathSequence(unit.unitId, unit.position);
          }
        }
      }

      // Detect changes from previous state
      const events = this.stateDiffDetector.detectChanges(state);

      // CRITICAL: Render units on the grid FIRST (other systems depend on sprite positions)
      await this.renderUnits(state);

      // TASK-VIZ-019: Manage health bars
      this.manageHealthBars(state, events);

      // TASK-VIZ-021: Manage projectiles
      this.manageProjectiles(state, events);

      // TASK-VIZ-022: Manage buff icons
      this.manageBuffIcons(state, events);

      // TASK-CAC-009: Process combat audio
      this.audioController.processStateUpdate(state);

      // Process events (both local diff events and server combat events)
      this.processEvents(events, state);
    } catch (error) {
      console.error('Error processing state update:', error);
    }
  }

  /**
   * Handle combat completed
   */
  private handleCombatCompleted(result: CombatResult): void {
    if (this.isDestroyed) {
      return;
    }

    // Flush any remaining buffered events so death animations play
    const remainingEvents = this.jitterBuffer.flush();
    this.processBufferedEvents(remainingEvents);

    // Linger on the final frame for 2.5 seconds so the player can see the result.
    // The render loop keeps running (death animations finish, particles settle)
    // before we tear down the visualization.
    this.combatEndTimerId = setTimeout(() => {
      this.combatEndTimerId = null;
      if (!this.isDestroyed) {
        this.stop();
      }
    }, 2500);
  }

  /**
   * Handle errors
   */
  private handleError(error: Error): void {
    console.error('Combat visualization error:', error);
  }

  /**
   * Handle socket disconnection
   * FIX: Prevents animation loop after socket disconnect by:
   * 1. Setting isSocketConnected to false
   * 2. Clearing all unit targets to prevent attack animations
   * 3. Transitioning all units to idle state
   */
  private handleSocketDisconnect(reason: string): void {
    console.warn('[CombatVisualizationManager] Socket disconnected:', reason);
    this.isSocketConnected = false;

    // Clear all unit targets to prevent attack animations from retriggering
    if (this.currentInterpolatedState?.units) {
      for (const unit of this.currentInterpolatedState.units) {
        unit.currentTarget = null;
        unit.attackCooldownRemaining = 999; // High value to prevent attack state
      }
    }

    // Transition all units to idle state to stop attack animations
    if (this.animationStateMachine) {
      const unitIds = this.animationStateMachine.getAllUnitIds();
      for (const unitId of unitIds) {
        this.animationStateMachine.updateState(unitId, {
          isMoving: false,
          isAttacking: false,
          health: 1 // Arbitrary positive value
        });
      }
    }

    console.log('[CombatVisualizationManager] Cleared attack state for all units after disconnect');
  }

  /**
   * Handle individual combat event from socket (L2-CLAUDE-ATTRS)
   * Routes events to appropriate handlers based on event type
   */
  private handleCombatEvent(event: any): void {
    if (!event || !event.type) {
      console.warn('[CombatVisualizationManager] Received invalid combat event:', event);
      return;
    }

    // Route event to appropriate handler based on type
    switch (event.type) {
      case 'ability_used':
        this.handleAbilityUsed(event);
        break;
      case 'ability_damage':
        this.handleAbilityDamage(event);
        break;
      case 'ability_heal':
        this.handleAbilityHeal(event);
        break;
      case 'ability_evaded':
        // Could show "MISS" or "DODGE" text
        console.log('[CombatVisualizationManager] Ability evaded:', event);
        break;
      case 'status_effect_applied':
        // Visual handled by ability_used
        console.log('[CombatVisualizationManager] Status effect applied:', event);
        break;
      case 'status_effect_tick':
        this.handleStatusEffectTick(event);
        break;
      case 'status_effect_expired':
        // Could remove status icon
        console.log('[CombatVisualizationManager] Status effect expired:', event);
        break;
      default:
        // Unknown event type - log for debugging
        console.log('[CombatVisualizationManager] Unknown combat event type:', event.type, event);
    }
  }

  /**
   * FIX: Handle batched combat:events from socket
   * This is the CORRECT way to receive damage_dealt events - they come via combat:events stream,
   * NOT via state.events (which is cleared before the state broadcast).
   */
  private handleCombatEvents(events: CombatEvent[]): void {
    if (!events || events.length === 0) return;
    this.jitterBuffer.enqueue(events);
  }

  /**
   * Process events released from the jitter buffer at a steady cadence.
   * Routes each event type to its existing handler method.
   */
  private processBufferedEvents(events: CombatEvent[]): void {
    if (events.length === 0) return;

    // Reuse existing attack/projectile handlers via synthetic state
    const attackOrProjectileEvents = events.filter(
      e => isAttackStartedEvent(e) || isProjectileLaunchedEvent(e)
    );
    if (attackOrProjectileEvents.length > 0 && this.currentState) {
      const syntheticState = {
        ...this.currentState,
        events: attackOrProjectileEvents,
      };
      this.handleAttackStartedEvents(syntheticState);
      this.handleProjectileSpawnedEvents(syntheticState);
    }

    const damageEvents = events.filter(isDamageDealtEvent);
    if (damageEvents.length > 0) {
      this.processDamageEventsFromStream(damageEvents);
    }

    const healingEvents = events.filter(e => e.eventType === 'healing_done');
    if (healingEvents.length > 0) {
      this.processHealingEventsFromStream(healingEvents);
    }

    const cleanseEvents = events.filter(isCleanseAppliedEvent);
    if (cleanseEvents.length > 0) {
      this.processCleanseEventsFromStream(cleanseEvents);
    }

    const shieldEvents = events.filter(isShieldAppliedEvent);
    if (shieldEvents.length > 0) {
      this.processShieldEventsFromStream(shieldEvents);
    }

    const reflectEvents = events.filter(isReflectTriggeredEvent);
    if (reflectEvents.length > 0) {
      this.processReflectEventsFromStream(reflectEvents);
    }

    const telegraphEvents = events.filter(isStrikeTelegraphEvent);
    if (telegraphEvents.length > 0) {
      this.processStrikeTelegraphEvents(telegraphEvents);
    }

    const impactEvents = events.filter(isStrikeImpactEvent);
    if (impactEvents.length > 0) {
      this.processStrikeImpactEvents(impactEvents);
    }

    const abilityEvents = events.filter(e => e.eventType === 'ability_used');
    if (abilityEvents.length > 0) {
      for (const event of abilityEvents) {
        this.handleAbilityUsed(event);
      }
    }

    const deathEvents = events.filter(e => e.eventType === 'unit_died');
    for (const event of deathEvents) {
      if (event.data?.unitId && event.data?.element) {
        this.deathElements.set(event.data.unitId as string, event.data.element as string);
      }
    }

    const waitingEvents = events.filter(isUnitWaitingEvent);
    if (waitingEvents.length > 0) {
      this.processWaitingEvents(waitingEvents);
    }
  }

  /**
   * FIX: Process damage events received from the combat:events stream
   * This replicates the logic from manageDamageNumbers but works with streamed events
   */
  private processDamageEventsFromStream(damageEvents: DamageDealtEvent[]): void {
    if (!this.currentState) {
      console.warn('[CombatVisualizationManager] No current state to process damage events');
      return;
    }

    // Phase 1 Foundation: When correlation is enabled, register damages and let update loop display them
    // When disabled (current default), proceed with immediate display (existing behavior)
    if (this.actionCorrelationService.isEnabled()) {
      for (const dmgEvent of damageEvents) {
        // Register with correlation service - it will queue until animation impact
        const readyDamage = this.actionCorrelationService.registerDamageDealt(
          dmgEvent.data,
          this.currentState.tick
        );
        // If returned immediately (disabled internally), display now
        if (readyDamage) {
          this.displayReadyDamage(readyDamage);
        }
      }
      // Note: Screen shake and other effects still triggered below for correlated damages
      // when they are released via update loop
      return;
    }

    // Calculate max intensity for screen shake (trigger once per batch)
    let maxDamageAmount = 0;
    let hasCritical = false;

    for (const dmgEvent of damageEvents) {
      maxDamageAmount = Math.max(maxDamageAmount, dmgEvent.data.damageAmount);
      if (dmgEvent.data.isCritical) hasCritical = true;
    }

    const tickShakeIntensity = hasCritical ? 'heavy' :
                               maxDamageAmount >= 30 ? 'heavy' :
                               maxDamageAmount >= 10 ? 'medium' : 'light';

    // Trigger screen shake once per batch
    this.screenShakeManager.shake(tickShakeIntensity);

    // Group damage by victim
    const damagesByVictim = new Map<string, DamageDealtEvent[]>();
    for (const dmgEvent of damageEvents) {
      const victimId = dmgEvent.data.victimId;
      const existing = damagesByVictim.get(victimId) || [];
      existing.push(dmgEvent);
      damagesByVictim.set(victimId, existing);
    }

    // Process each victim's damage
    for (const [victimId, damages] of damagesByVictim) {
      const victim = this.currentState.units.find(u => u.unitId === victimId);
      if (!victim) continue;

      // Get sprite position for effects
      // NOTE: sprite.x/y is relative to container (always 0), container.x/y is world position
      const spriteData = this.gridRenderer.getSpriteByUnitId(victim.unitId);
      if (!spriteData?.container) continue;

      const position = {
        x: spriteData.container.x,
        y: spriteData.container.y
      };

      // Calculate hit intensity
      const maxDamage = Math.max(...damages.map(d => d.data.damageAmount));
      const hasCrit = damages.some(d => d.data.isCritical);
      const hitIntensity = hasCrit ? 'heavy' :
                          maxDamage >= 30 ? 'heavy' :
                          maxDamage >= 10 ? 'medium' : 'light';

      // Trigger hit stop
      const primaryAttacker = damages[0].data.attackerId;
      this.hitStopManager.trigger(primaryAttacker, victimId, hitIntensity);
      this.hitStopManager.triggerStatePause(maxDamage);

      // VIS-002-A/B: Flash sprite on damage
      // White for normal damage, orange for critical hits
      // Increased duration for better visibility (150ms normal, 200ms crit)
      const flashColor = hasCrit ? 0xFF6600 : 0xFFFFFF;
      const flashDuration = hasCrit ? 200 : 150;
      this.gridRenderer.flashSprite(victimId, flashColor, flashDuration);

      // VIS-006-A: Apply knockback to defender (critical hits get larger knockback)
      this.applyKnockback(victimId, primaryAttacker, hasCrit);

      // VIS-006-B: Apply lunge to attacker toward target
      this.applyAttackLunge(primaryAttacker, victimId);

      // Spawn damage numbers
      for (let i = 0; i < damages.length; i++) {
        const damageEvent = damages[i];
        const offset = i * 20;

        // L4-BATCH1-007: Determine damage display type - execute takes priority
        const displayType = damageEvent.data.isExecute
          ? DamageType.EXECUTE
          : this.mapDamageTypeToDisplay(damageEvent.data.damageType);

        const damageNumber = this.damageNumberPool.acquire(
          damageEvent.data.damageAmount,
          displayType,
          { x: position.x, y: position.y - offset }
        );

        console.log(`[CombatVisualizationManager] Damage number spawned: ${damageEvent.data.damageAmount} (${damageEvent.data.damageType}${damageEvent.data.isExecute ? ' EXECUTE' : ''})`);

        this.effectContainers.damageNumbers.addChild(damageNumber.container);
        this.activeDamageNumbers.push(damageNumber);

        // L4-BATCH1-007: Trigger execute effect animation on execute damage
        if (damageEvent.data.isExecute) {
          const frames = this.libraryEffectService.getCachedFrames('execute_effect');
          if (frames && this.effectLayer) {
            this.effectLayer.triggerEffect(
              'execute_effect',
              frames,
              position,
              { scale: 2.5, blendMode: 'add' }
            );
          }
        }
      }

      // Trigger health bar flash
      const healthBar = this.healthBars.get(victimId);
      if (healthBar) {
        this.healthBarEffectsManager.triggerFlash(victimId, healthBar);
      }
    }
  }

  /**
   * Phase 1 Foundation: Display a single ready damage (from correlation service)
   * Used when correlation is enabled and damages are released via update loop
   */
  private displayReadyDamage(damage: ReadyDamage): void {
    if (!this.currentState) return;

    const victim = this.currentState.units.find(u => u.unitId === damage.victimId);
    if (!victim) return;

    const spriteData = this.gridRenderer.getSpriteByUnitId(damage.victimId);
    if (!spriteData?.container) return;

    const position = {
      x: spriteData.container.x,
      y: spriteData.container.y
    };

    // Determine damage display type
    const displayType = damage.isExecute
      ? DamageType.EXECUTE
      : this.mapDamageTypeToDisplay(damage.damageType);

    // Spawn damage number
    const damageNumber = this.damageNumberPool.acquire(
      damage.damageAmount,
      displayType,
      position
    );

    this.effectContainers.damageNumbers.addChild(damageNumber.container);
    this.activeDamageNumbers.push(damageNumber);

    // Trigger combat juice effects
    const hitIntensity = damage.isCritical ? 'heavy' :
                        damage.damageAmount >= 30 ? 'heavy' :
                        damage.damageAmount >= 10 ? 'medium' : 'light';

    this.hitStopManager.trigger(damage.attackerId, damage.victimId, hitIntensity);
    this.hitStopManager.triggerStatePause(damage.damageAmount);
    this.screenShakeManager.shake(hitIntensity);

    // Flash sprite on damage
    const flashColor = damage.isCritical ? 0xFF6600 : 0xFFFFFF;
    const flashDuration = damage.isCritical ? 200 : 150;
    this.gridRenderer.flashSprite(damage.victimId, flashColor, flashDuration);

    // Apply knockback and lunge
    this.applyKnockback(damage.victimId, damage.attackerId, damage.isCritical);
    this.applyAttackLunge(damage.attackerId, damage.victimId);

    // Trigger health bar flash
    const healthBar = this.healthBars.get(damage.victimId);
    if (healthBar) {
      this.healthBarEffectsManager.triggerFlash(damage.victimId, healthBar);
    }

    // Execute effect animation
    if (damage.isExecute && this.effectLayer) {
      const frames = this.libraryEffectService.getCachedFrames('execute_effect');
      if (frames) {
        this.effectLayer.triggerEffect(
          'execute_effect',
          frames,
          position,
          { scale: 2.5, blendMode: 'add' }
        );
      }
    }

    console.log(`[CombatVisualizationManager] Correlated damage displayed: ${damage.damageAmount} (${damage.damageType}${damage.isExecute ? ' EXECUTE' : ''})`);
  }

  /**
   * Phase 1 Foundation: Update action correlation service and display ready damages
   * Called every frame from renderFrame() - only active when correlation is enabled
   */
  private updateCorrelatedDamages(deltaTime: number): void {
    // Skip if correlation is disabled (default)
    if (!this.actionCorrelationService.isEnabled()) {
      return;
    }

    // Convert deltaTime from seconds to milliseconds for the service
    const deltaMs = deltaTime * 1000;

    // Get damages that are ready for display
    const readyDamages = this.actionCorrelationService.update(deltaMs);

    if (readyDamages.length > 0) {
      // Pass through display queue for pacing
      this.damageDisplayQueue.enqueueMultiple(readyDamages, performance.now());
    }

    // Get paced damages from queue
    const pacedDamages = this.damageDisplayQueue.getReadyDamages();

    // Display each ready damage
    for (const damage of pacedDamages) {
      this.displayReadyDamage(damage);
    }
  }

  /**
   * FIX: Process healing events received from the combat:events stream
   * L4-BATCH1-008: Differentiates lifesteal healing from regular healing
   */
  private processHealingEventsFromStream(healingEvents: CombatEvent[]): void {
    if (!this.currentState) return;

    for (const healEvent of healingEvents) {
      const data = healEvent.data as HealingDoneEventData;
      const unit = this.currentState.units.find(u => u.unitId === data.targetUnitId);
      if (!unit) continue;

      // NOTE: sprite.x/y is relative to container (always 0), container.x/y is world position
      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);
      if (!spriteData?.container) continue;

      const position = {
        x: spriteData.container.x,
        y: spriteData.container.y
      };

      // L4-BATCH1-008: Use LIFESTEAL type for lifesteal healing, HEAL for everything else
      const healType = data.healType === 'lifesteal' ? DamageType.LIFESTEAL : DamageType.HEAL;

      const healNumber = this.damageNumberPool.acquire(
        data.healAmount,
        healType,
        position
      );

      this.effectContainers.damageNumbers.addChild(healNumber.container);
      this.activeDamageNumbers.push(healNumber);

      // L4-BATCH1-008: Log lifesteal healing and trigger effect animation
      if (data.healType === 'lifesteal') {
        console.log(`[CombatVisualizationManager] Lifesteal heal: +${data.healAmount} on ${data.targetUnitId.substring(0, 20)}`);

        // Trigger lifesteal effect animation
        const frames = this.libraryEffectService.getCachedFrames('lifesteal_effect');
        if (frames && this.effectLayer) {
          this.effectLayer.triggerEffect(
            'lifesteal_effect',
            frames,
            position,
            { scale: 2.0, blendMode: 'add' }
          );
        }
      }
    }
  }

  /**
   * L4-BATCH1-009: Process cleanse events received from the combat:events stream
   * Shows a "Cleansed!" floating text on the cleansed unit with effect count
   */
  private processCleanseEventsFromStream(cleanseEvents: CombatEvent[]): void {
    if (!this.currentState) return;

    for (const cleanseEvent of cleanseEvents) {
      const data = cleanseEvent.data as CleanseAppliedEventData;
      const unit = this.currentState.units.find(u => u.unitId === data.targetUnitId);
      if (!unit) continue;

      // NOTE: sprite.x/y is relative to container (always 0), container.x/y is world position
      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);
      if (!spriteData?.container) continue;

      const position = {
        x: spriteData.container.x,
        y: spriteData.container.y
      };

      // Create cleanse visual - shows "Cleansed! (count)" in white/gold
      const cleanseNumber = this.damageNumberPool.acquire(
        data.cleansedEffectCount,
        DamageType.CLEANSE,
        position
      );

      this.effectContainers.damageNumbers.addChild(cleanseNumber.container);
      this.activeDamageNumbers.push(cleanseNumber);

      console.log(`[CombatVisualizationManager] Cleanse effect: ${data.cleansedEffectCount} debuffs removed from ${data.targetUnitId.substring(0, 20)}`);

      // L4-BATCH1-009: Trigger cleanse effect animation
      const frames = this.libraryEffectService.getCachedFrames('cleanse_effect');
      if (frames && this.effectLayer) {
        this.effectLayer.triggerEffect(
          'cleanse_effect',
          frames,
          position,
          { scale: 2.5, blendMode: 'add' }
        );
      }
    }
  }

  /**
   * L4-BATCH2: Process shield applied events
   * Triggers shield effect animation when a shield is applied
   */
  private processShieldEventsFromStream(shieldEvents: CombatEvent[]): void {
    if (!this.currentState) return;

    for (const shieldEvent of shieldEvents) {
      const data = shieldEvent.data as ShieldAppliedEventData;
      const unit = this.currentState.units.find(u => u.unitId === data.targetId);
      if (!unit) continue;

      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);
      if (!spriteData?.container) continue;

      const position = {
        x: spriteData.container.x,
        y: spriteData.container.y
      };

      console.log(`[CombatVisualizationManager] Shield applied: ${data.amount} on ${data.targetId.substring(0, 20)}`);

      // Trigger shield effect animation
      const frames = this.libraryEffectService.getCachedFrames('shield_effect');
      if (frames && this.effectLayer) {
        this.effectLayer.triggerEffect(
          'shield_effect',
          frames,
          position,
          { scale: 3.0, blendMode: 'add' }
        );
      }
    }
  }

  /**
   * L4-BATCH2: Process reflect triggered events
   * Triggers reflect effect animation when damage is reflected
   */
  private processReflectEventsFromStream(reflectEvents: CombatEvent[]): void {
    if (!this.currentState) return;

    for (const reflectEvent of reflectEvents) {
      const data = reflectEvent.data as ReflectTriggeredEventData;
      // The unit that had reflect (and triggered it)
      const reflectorUnit = this.currentState.units.find(u => u.unitId === data.targetId);
      if (!reflectorUnit) continue;

      const spriteData = this.gridRenderer.getSpriteByUnitId(reflectorUnit.unitId);
      if (!spriteData?.container) continue;

      const position = {
        x: spriteData.container.x,
        y: spriteData.container.y
      };

      console.log(`[CombatVisualizationManager] Reflect triggered: ${data.damageReflected} damage reflected from ${data.targetId.substring(0, 20)}`);

      // Trigger reflect effect animation on the reflector
      const frames = this.libraryEffectService.getCachedFrames('reflect_effect');
      if (frames && this.effectLayer) {
        this.effectLayer.triggerEffect(
          'reflect_effect',
          frames,
          position,
          { scale: 2.5, blendMode: 'add' }
        );
      }
    }
  }

  /**
   * Process unit_waiting events - show waiting indicator on blocked units
   * L4-COLL-016
   */
  private processWaitingEvents(events: CombatEvent[]): void {
    for (const event of events) {
      if (!isUnitWaitingEvent(event)) continue;

      const data = event.data as UnitWaitingEventData;
      const spriteData = this.gridRenderer.getSpriteByUnitId(data.unitId);

      if (spriteData) {
        // Show waiting indicator (simple approach: tint)
        this.showWaitingIndicator(data.unitId, data.reason);
      }
    }
  }

  /**
   * Show visual indicator that unit is waiting/blocked
   * L4-COLL-017
   */
  private showWaitingIndicator(unitId: string, reason: string): void {
    const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
    if (!spriteData?.sprite) return;

    // Apply a slight gray/blue tint to indicate waiting
    spriteData.sprite.tint = 0xaaaaff; // Light blue tint

    // Clear tint after a short delay (will be refreshed if still waiting)
    setTimeout(() => {
      this.clearWaitingIndicator(unitId);
    }, 500);
  }

  /**
   * Clear waiting indicator when unit moves or waiting state ends
   * L4-COLL-017
   */
  private clearWaitingIndicator(unitId: string): void {
    const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
    if (spriteData?.sprite) {
      spriteData.sprite.tint = 0xffffff; // Reset to normal color
    }
  }

  /**
   * L4-BATCH4: Map element + origin to strike effect animation ID
   * Returns the appropriate library animation for the strike type
   */
  private getStrikeEffectId(element: string, origin: 'sky' | 'ground', abilityId?: string): string {
    // Check ability ID first for specific mappings
    if (abilityId) {
      const abilityMap: Record<string, string> = {
        'lightning_strike': 'lightning_strike_effect',
        'lightningStrike': 'lightning_strike_effect',
        'meteor': 'meteor_effect',
        'earthquake': 'earthquake_effect',
        'rain_of_arrows': 'arrow_rain_effect',
        'rainOfArrows': 'arrow_rain_effect',
        'holy_smite': 'holy_smite_effect',
        'holySmite': 'holy_smite_effect',
        'volcanic_eruption': 'volcanic_eruption_effect',
        'volcanicEruption': 'volcanic_eruption_effect',
        'ice_storm': 'ice_storm_effect',
        'iceStorm': 'ice_storm_effect',
        'shadow_bolt_barrage': 'shadow_bolt_effect',
        'shadowBoltBarrage': 'shadow_bolt_effect',
      };
      if (abilityMap[abilityId]) {
        return abilityMap[abilityId];
      }
    }

    // Fallback: Map by element + origin
    const elementMap: Record<string, string> = {
      'lightning': 'lightning_strike_effect',
      'fire': origin === 'ground' ? 'volcanic_eruption_effect' : 'meteor_effect',
      'nature': 'earthquake_effect',
      'physical': 'arrow_rain_effect',
      'holy': 'holy_smite_effect',
      'ice': 'ice_storm_effect',
      'dark': 'shadow_bolt_effect',
      'arcane': 'shadow_bolt_effect',
    };

    return elementMap[element] || 'meteor_effect'; // Default to meteor if unknown
  }

  /**
   * L4-BATCH4: Process strike telegraph events
   * Shows warning indicators on hexes before strike impact
   */
  private processStrikeTelegraphEvents(telegraphEvents: CombatEvent[]): void {
    if (!this.currentState || !this.gridRenderer) return;

    for (const event of telegraphEvents) {
      const data = event.data as StrikeTelegraphEventData;

      console.log(`[CombatVisualizationManager] Strike telegraph: ${data.abilityId} at hex (${data.centerHex.q}, ${data.centerHex.r}) - ${data.affectedHexes.length} hexes affected`);

      // Get telegraph frames
      const frames = this.libraryEffectService.getCachedFrames('strike_telegraph');
      if (!frames || !this.effectLayer) continue;

      // Show telegraph on each affected hex
      for (const hex of data.affectedHexes) {
        const pixelPos = this.gridRenderer.hexToPixel(hex);
        if (pixelPos) {
          this.effectLayer.triggerEffect(
            'strike_telegraph',
            frames,
            { x: pixelPos.x, y: pixelPos.y },
            {
              scale: 1.5,
              blendMode: 'add',
              // Duration matches telegraph time (ticksUntilImpact / tick rate)
              // Default animation handles timing
            }
          );
        }
      }
    }
  }

  /**
   * L4-BATCH4: Process strike impact events
   * Plays the appropriate impact animation at strike location
   */
  private processStrikeImpactEvents(impactEvents: CombatEvent[]): void {
    if (!this.currentState || !this.gridRenderer) return;

    for (const event of impactEvents) {
      const data = event.data as StrikeImpactEventData;

      // Get the appropriate effect based on element/ability
      const effectId = this.getStrikeEffectId(data.element, data.origin, data.abilityId);

      console.log(`[CombatVisualizationManager] Strike impact: ${data.abilityId} (${effectId}) at hex (${data.centerHex.q}, ${data.centerHex.r}) - ${data.unitsHit.length} units hit`);

      // Get effect frames
      const frames = this.libraryEffectService.getCachedFrames(effectId);
      if (!frames || !this.effectLayer) continue;

      // Play impact effect at center hex
      const centerPixelPos = this.gridRenderer.hexToPixel(data.centerHex);
      if (centerPixelPos) {
        // Determine scale based on number of affected hexes (larger AOE = larger effect)
        const aoeScale = Math.min(3.5, 2.0 + (data.affectedHexes.length * 0.1));

        this.effectLayer.triggerEffect(
          effectId,
          frames,
          { x: centerPixelPos.x, y: centerPixelPos.y },
          {
            scale: aoeScale,
            blendMode: 'add',
          }
        );

        // Trigger screen shake for dramatic impact
        const shakeIntensity = data.unitsHit.length >= 3 ? 'heavy' :
                              data.unitsHit.length >= 1 ? 'medium' : 'light';
        this.screenShakeManager.shake(shakeIntensity);
      }
    }
  }

  /**
   * TASK-EFD-003: Handle effectFrames reliable delivery from server
   * Caches received effectFrames for all units and preloads textures
   */
  private handleEffectFrames(data: EffectFramesData): void {
    console.log(`[CombatVisualizationManager] Received effectFrames for ${Object.keys(data.frames).length} units (matchId: ${data.matchId})`);

    // Store matchId for request fallback
    this.currentMatchId = data.matchId;

    // Reset pending request flag since we received frames
    this.pendingEffectFramesRequest = false;

    for (const [unitId, attrs] of Object.entries(data.frames)) {
      if (attrs && attrs.length > 0) {
        this.unitVisualAttributes.set(unitId, attrs);
        console.log(`[CombatVisualizationManager] Cached ${attrs.length} visual attributes for unit ${unitId.substring(0, 30)}`);

        // Preload effect frames if effectLayer exists
        if (this.effectLayer && !this.preloadedEffectFrames.has(unitId)) {
          this.preloadedEffectFrames.add(unitId);
          this.effectLayer.preloadEffectFrames(attrs).catch((err) => {
            console.error(`[CombatVisualizationManager] Failed to preload effect frames for unit ${unitId}:`, err);
          });
        }
      }
    }

    console.log(`[CombatVisualizationManager] EffectFrames cache now has ${this.unitVisualAttributes.size} units`);
  }

  /**
   * Handle ability_used event - play ability visual effect (L2-CLAUDE-ATTRS)
   * L4-EFFECT-001: Enhanced to handle strike abilities with hex-based targeting
   */
  private handleAbilityUsed(event: any): void {
    const factory = getAbilityEffectFactory();
    if (!factory) return;

    // L3-VIS-SUBHEX-001: Use visual position (includes sub-hex offset) for effects
    const casterUnit = this.getUnitVisualPosition(event.data.casterId);
    if (!casterUnit) return;

    // Determine target position - either from unit or from hex coordinates
    let targetX: number;
    let targetY: number;

    // L3-VIS-SUBHEX-001: Use visual position for targeting (projectiles hit sprite, not hex center)
    const targetUnit = event.data.targetId ? this.getUnitVisualPosition(event.data.targetId) : null;

    if (targetUnit) {
      // Standard case: target is a unit
      targetX = targetUnit.x;
      targetY = targetUnit.y;
    } else if (event.data.targetHex) {
      // Strike ability case: target is a hex position (L4-EFFECT-001)
      const targetPixels = this.hexToPixel(event.data.targetHex);
      targetX = targetPixels.x;
      targetY = targetPixels.y;
    } else if (event.data.targetType === 'self') {
      // Self-targeted ability
      targetX = casterUnit.x;
      targetY = casterUnit.y;
    } else {
      // No valid target - skip effect
      console.warn('[CombatVisualizationManager] ability_used missing target:', event.data);
      return;
    }

    const request: AbilityEffectRequest = {
      abilityId: event.data.abilityId,
      abilityName: event.data.abilityName,
      element: event.data.element || 'physical',
      delivery: event.data.delivery || 'melee',
      effects: [], // Effects will trigger separate events
      startX: casterUnit.x,
      startY: casterUnit.y,
      targetX: targetX,
      targetY: targetY,
      aoeRadius: event.data.aoeRadius,
      targetType: event.data.targetType,
      strikeOrigin: event.data.strikeOrigin, // L4-EFFECT-001: For strike animations
      effectKeys: event.data.effectKeys // L4-EFFECT-006: Effect Gallery sprite keys
    };

    factory.playAbilityEffect(request);

    // L3-SCREEN-EFFECTS: Trigger screen effects for impactful abilities
    const element = event.data.element;
    const aoeRadius = event.data.aoeRadius;

    // Lightning and Holy always trigger heavy shake + flash
    if (element === 'lightning' || element === 'holy') {
      this.screenShakeManager.shake('heavy');
      this.triggerScreenFlash(element === 'lightning' ? 0xFFFFDD : 0xFFFFF0);
      console.log(`[CombatVisualizationManager] Screen effects triggered for ${element} ability`);
    }
    // Large AOE abilities trigger medium shake
    else if (aoeRadius && aoeRadius >= 4) {
      this.screenShakeManager.shake('medium');
      console.log(`[CombatVisualizationManager] Medium shake triggered for large AOE (radius: ${aoeRadius})`);
    }

    // L3-LINGERING-HAZARDS: Spawn hazard zones for breath and AOE abilities
    const delivery = event.data.delivery || 'melee';
    if (this.hazardZoneRenderer && (delivery === 'breath' || delivery === 'breath-particle' || delivery === 'aoe')) {
      this.hazardZoneRenderer.spawnHazard({ x: targetX, y: targetY }, element || 'physical');
      console.log(`[CombatVisualizationManager] Spawned ${element || 'physical'} hazard at (${targetX}, ${targetY})`);
    }
  }

  /**
   * Trigger a screen flash effect (L3-SCREEN-EFFECTS)
   * Creates a full-screen overlay that fades from the specified color
   * @param color - Hex color for the flash (default: white)
   * @param duration - Duration in ms for the flash to fade (default: 150ms)
   */
  private triggerScreenFlash(color: number = 0xFFFFFF, duration: number = 150): void {
    if (!this.flashOverlay || !this.app) return;

    // Cancel any existing flash animation
    if (this.flashAnimationId) {
      cancelAnimationFrame(this.flashAnimationId);
      this.flashAnimationId = null;
    }

    const startTime = Date.now();
    const width = this.app.renderer.width;
    const height = this.app.renderer.height;

    const animate = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= duration) {
        this.flashOverlay!.clear();
        this.flashAnimationId = null;
        return;
      }

      const progress = elapsed / duration;
      const alpha = (1 - progress) * 0.6; // Fade from 60% to 0%

      this.flashOverlay!.clear();
      this.flashOverlay!.rect(0, 0, width, height);
      this.flashOverlay!.fill({ color, alpha });

      this.flashAnimationId = requestAnimationFrame(animate);
    };

    animate();
  }

  /**
   * Handle ability_damage event - show damage number with element color (L2-CLAUDE-ATTRS)
   */
  private handleAbilityDamage(event: any): void {
    // L3-VIS-SUBHEX-001: Use visual position for damage numbers (appears at sprite position)
    const targetUnit = this.getUnitVisualPosition(event.data.targetId);
    if (!targetUnit) return;

    // Create damage number from pool
    const damageType = event.data.isCritical ? DamageType.CRITICAL : DamageType.PHYSICAL;
    const damageNumber = this.damageNumberPool.acquire(
      event.data.damage,
      damageType,
      { x: targetUnit.x, y: targetUnit.y }
    );

    // Add to container and tracking array
    this.effectContainers.damageNumbers.addChild(damageNumber.container);
    this.activeDamageNumbers.push(damageNumber);
  }

  /**
   * Handle ability_heal event - show healing number (L2-CLAUDE-ATTRS)
   */
  private handleAbilityHeal(event: any): void {
    // L3-VIS-SUBHEX-001: Use visual position for heal numbers (appears at sprite position)
    const targetUnit = this.getUnitVisualPosition(event.data.targetId);
    if (!targetUnit) return;

    // Create healing number from pool
    const healNumber = this.damageNumberPool.acquire(
      event.data.healing,
      DamageType.HEAL,
      { x: targetUnit.x, y: targetUnit.y }
    );

    // Add to container and tracking array
    this.effectContainers.damageNumbers.addChild(healNumber.container);
    this.activeDamageNumbers.push(healNumber);
  }

  /**
   * Handle status_effect_tick - show small tick damage/heal (L2-CLAUDE-ATTRS)
   */
  private handleStatusEffectTick(event: any): void {
    // L3-VIS-SUBHEX-001: Use visual position for tick numbers (appears at sprite position)
    const targetUnit = this.getUnitVisualPosition(event.data.targetId);
    if (!targetUnit) return;

    // Create tick number from pool (offset slightly)
    const type = event.data.effectType === 'hot' ? DamageType.HEAL : DamageType.MAGICAL;
    const tickNumber = this.damageNumberPool.acquire(
      event.data.value,
      type,
      { x: targetUnit.x, y: targetUnit.y - 10 }
    );

    // Add to container and tracking array
    this.effectContainers.damageNumbers.addChild(tickNumber.container);
    this.activeDamageNumbers.push(tickNumber);
  }

  /**
   * Process detected events
   */
  private processEvents(events: any, state: CombatState): void {
    // Process server combat events through the event handler
    // This enables movement ability visuals (L3-MOVEMENT-ABILITIES)
    if (state.events && state.events.length > 0) {
      this.combatEventHandler.processEvents(state.events);
    }

    // Placeholder - local diff events delegation will be implemented in integration tasks
    // TASK-VIZ-019: Health bars
    // TASK-VIZ-020: Damage numbers
    // TASK-VIZ-021: Projectiles
    // TASK-VIZ-022: Buff icons
  }

  /**
   * Render all units from combat state on the grid
   * This is the critical integration between combat state and visual rendering
   *
   * IMPORTANT: Units are rendered at their PREVIOUS positions (if available) to allow
   * smooth interpolation to their current positions via renderFrame()
   */
  private async renderUnits(state: CombatState): Promise<void> {
    // DIAGNOSTIC: Log unit breakdown and sprite data availability
    const player1Units = state.units.filter(u => u.ownerId === 'player1');
    const player2Units = state.units.filter(u => u.ownerId === 'player2');
    const player1WithSprites = player1Units.filter(u => this.creatureDataMap.has(u.creatureId));
    const player2WithSprites = player2Units.filter(u => this.creatureDataMap.has(u.creatureId));

    console.log('[CombatVisualizationManager] renderUnits DIAGNOSTIC:', {
      totalUnits: state.units.length,
      player1: player1Units.length,
      player2: player2Units.length,
      player1WithSprites: player1WithSprites.length,
      player2WithSprites: player2WithSprites.length,
      creatureDataMapSize: this.creatureDataMap.size,
      creatureDataMapKeys: Array.from(this.creatureDataMap.keys()).slice(0, 5) // First 5 keys
    });

    // Log specific player2 units for debugging
    if (player2Units.length > 0 && player2WithSprites.length === 0) {
      console.error('[CombatVisualizationManager] CRITICAL: Player2 units have NO sprite data!', {
        player2CreatureIds: player2Units.map(u => u.creatureId),
        mapHasAny: player2Units.some(u => this.creatureDataMap.has(u.creatureId))
      });
    }

    // Render all alive units AND dying units (TASK-DEATH-004: keep dying units visible)
    for (const unit of state.units) {
      // TASK-DEATH-004: Render unit if alive OR if in death sequence
      const isAlive = unit.status === 'alive';
      const isDying = this.deathSequenceManager.isUnitDying(unit.unitId);

      if (isAlive || isDying) {
        // Look up creature sprite data from the map
        const creatureData = this.creatureDataMap.get(unit.creatureId);

        if (!creatureData) {
          console.warn('[CombatVisualizationManager] No sprite data for creature:', unit.creatureId, 'owner:', unit.ownerId);
        }

        try {
          // Calculate creature size multiplier
          // New creatures: Uses Claude-assigned size (0.3-3.0) + ±10% variation
          // Legacy creatures: Falls back to archetype-based sizing
          let sizeMultiplier = calculateCreatureSizeMultiplier(
            unit.unitId,
            unit.stats.archetype,
            creatureData?.size // Pass stored size if available
          );

          // L3-MULTI-HEX-FIX: For multi-hex creatures, keep the creature's actual
          // visual size (sizeMultiplier) — the renderer will NOT multiply by footprintSize.
          // Footprint determines occupancy/separation only, not visual sprite scale.
          // No override needed — sizeMultiplier already reflects the creature's visual size.

          // Use unitId-based rendering if available (combat-specific)
          if (this.gridRenderer.renderCreatureWithId) {
            // L3-MULTI-HEX: Merge footprint from combat unit into sprite data
            // Footprint comes from combat state (unit), not deployment cache (creatureData)
            const spriteDataWithFootprint = unit.footprint
              ? { ...creatureData, footprint: unit.footprint }
              : creatureData;

            // Debug log for multi-hex units
            if (unit.footprint && unit.footprint.size > 1) {
              console.log(`[CombatVisualizationManager] Multi-hex unit ${unit.unitId}: footprint size=${unit.footprint.size}`);
            }

            // Convert backend degree-based facingDirection to HexDirection enum
            const hexDir = unit.facingDirection != null
              ? degreesToHexDirection(unit.facingDirection)
              : 1; // Default to East

            this.gridRenderer.renderCreatureWithId(
              unit.unitId,
              unit.position,
              unit.creatureId,
              unit.ownerId,
              spriteDataWithFootprint, // spriteData - includes sprites AND footprint from combat state
              1.0, // full opacity
              hexDir, // facing direction (HexDirection enum)
              sizeMultiplier // size variety based on archetype
            );
          } else {
            // Convert backend degree-based facingDirection to HexDirection enum
            const hexDir = unit.facingDirection != null
              ? degreesToHexDirection(unit.facingDirection)
              : 1; // Default to East

            // Fallback to position-based rendering
            this.gridRenderer.renderCreature(
              unit.position,
              unit.creatureId,
              unit.ownerId,
              creatureData,
              1.0,
              hexDir
            );
          }

          // Track that this unit has a sprite (TASK-RENDER-007)
          this.activeSpriteUnits.add(unit.unitId);

          // Register unit with animation state machine (TASK-COMBAT-VIZ-007)
          if (this.animationStateMachine.getState(unit.unitId) === null) {
            this.animationStateMachine.registerUnit(unit);
            console.log('[CombatVisualizationManager] Registered unit with animation state machine:', unit.unitId);
          }

          // TASK-ATTR-016: Store visual attributes for this unit
          // OOM-FIX: Only update cache if effectFrames are present (backend strips after first send)
          // This prevents overwriting cached frames with undefined values
          const hasEffectFrames = unit.visualAttributes?.some(attr => attr.effectFrames && attr.effectFrames.length > 0);
          const alreadyCached = this.unitVisualAttributes.has(unit.unitId);

          if (unit.visualAttributes && unit.visualAttributes.length > 0 && (hasEffectFrames || !alreadyCached)) {
            // Only update if we have effectFrames OR we haven't cached yet
            if (hasEffectFrames) {
              this.unitVisualAttributes.set(unit.unitId, unit.visualAttributes);
              console.log(`[CombatVisualizationManager] Stored ${unit.visualAttributes.length} visual attributes for unit:`, unit.unitId);
              // DEBUG: Log detailed attribute info
              unit.visualAttributes.forEach((attr, idx) => {
                console.log(`  [DEBUG] Attr ${idx}: ${attr.name}, damageType: ${attr.damageType}, frames: ${attr.effectFrames?.length || 0}`);
              });
            }

            // PIXIJS-V8-FIX: Preload effect frames asynchronously for this unit
            // This ensures textures are ready before effect triggers
            if (this.effectLayer && !this.preloadedEffectFrames.has(unit.unitId) && hasEffectFrames) {
              this.preloadedEffectFrames.add(unit.unitId);
              this.effectLayer.preloadEffectFrames(unit.visualAttributes).catch((err) => {
                console.error(`[CombatVisualizationManager] Failed to preload effect frames for unit ${unit.unitId}:`, err);
              });
            }
          } else if (!alreadyCached) {
            console.warn(`[CombatVisualizationManager] NO visualAttributes for unit ${unit.unitId} (creatureId: ${unit.creatureId})`);
          }

          console.log('[CombatVisualizationManager] Rendered unit:', {
            unitId: unit.unitId,
            creatureId: unit.creatureId,
            position: unit.position,
            ownerId: unit.ownerId,
            hasSpriteData: !!creatureData,
            hasVisualAttributes: !!(unit.visualAttributes && unit.visualAttributes.length > 0)
          });
        } catch (error) {
          console.error('[CombatVisualizationManager] Error rendering unit:', unit.unitId, error);
        }
      }
    }
  }

  /**
   * Manage health bars for units in combat (TASK-VIZ-019)
   */
  private manageHealthBars(state: CombatState, events: any): void {
    const currentTime = Date.now();

    console.log(`[CombatVisualizationManager] manageHealthBars - TICK ${state.tick} - events.damages: ${events.damages?.length || 0}, state.units: ${state.units.length}`);

    // DEBUG: Log unit details (every 60 ticks to avoid spam, plus first 5 ticks)
    if (state.tick <= 5 || state.tick % 60 === 0) {
      for (const unit of state.units) {
        console.log(`[CombatVisualizationManager] TICK ${state.tick} Unit:`, {
          unitId: unit.unitId.substring(0, 30),
          position: unit.position,
          health: `${unit.health}/${unit.maxHealth}`,
          currentTarget: unit.currentTarget ? unit.currentTarget.substring(0, 30) : 'NONE',
          spawnTicksRemaining: unit.spawnTicksRemaining,
          status: unit.status
        });
      }
      console.log(`[CombatVisualizationManager] TICK ${state.tick} - unitsInCombat.size:`, this.unitsInCombat.size);
    }

    // Track units entering combat
    // 1. From damage events - mark victims as in combat
    for (const damage of events.damages || []) {
      console.log('[CombatVisualizationManager] Damage event detected for unit:', damage.unitId);
      this.unitsInCombat.set(damage.unitId, currentTime);
    }

    // 2. From units with currentTarget (may be moving to target or attacking)
    for (const unit of state.units) {
      if (unit.currentTarget) {
        const wasInCombat = this.unitsInCombat.has(unit.unitId);
        this.unitsInCombat.set(unit.unitId, currentTime);

        // Log when units first enter combat via currentTarget
        if (!wasInCombat && (state.tick <= 5 || state.tick % 60 === 0)) {
          console.log(`[CombatVisualizationManager] TICK ${state.tick} - Unit ENTERED COMBAT via currentTarget:`, unit.unitId.substring(0, 30));
        }
      }
    }

    // Animation updates moved to renderFrame() for 60 FPS sync (TASK-TIMING-002)

    // Create/update health bars for ALL living units (not just "in combat")
    // Health bars should be visible from battle start for team identification
    for (const unit of state.units) {
      // Skip dead units
      if (unit.status === 'dead' || unit.health <= 0) {
        continue;
      }

      // Track units entering combat for other purposes
      if (unit.currentTarget) {
        this.unitsInCombat.set(unit.unitId, currentTime);
      }

      // Get sprite by unitId (FIX: sprites are keyed by unitId, not hex hash)
      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);

      let position: { x: number; y: number };

      if (spriteData?.sprite) {
        // Use sprite position if available
        position = {
          x: spriteData.sprite.x,
          y: spriteData.sprite.y - this.HEALTH_BAR_OFFSET_Y
        };
      } else if (unit.position) {
        // Fallback: Calculate position from hex coordinates (for async-loading sprites)
        // This ensures health bars appear even before sprite textures finish loading
        const hexPosition = this.gridRenderer.hexToPixel(unit.position);
        position = {
          x: hexPosition.x,
          y: hexPosition.y - this.HEALTH_BAR_OFFSET_Y
        };
        // Only log fallback usage periodically
        if (state.tick <= 5 || state.tick % 60 === 0) {
          console.log(`[CombatVisualizationManager] TICK ${state.tick} - Using hex position fallback for health bar:`, {
            unitId: unit.unitId.substring(0, 30),
            position: unit.position
          });
        }
      } else {
        // No sprite and no position - skip this unit
        if (state.tick <= 5 || state.tick % 60 === 0) {
          console.warn(`[CombatVisualizationManager] TICK ${state.tick} - No sprite or position for unit:`, {
            unitId: unit.unitId.substring(0, 30)
          });
        }
        continue;
      }

      // Get or create health bar
      let healthBar = this.healthBars.get(unit.unitId);

      if (!healthBar) {
        // Create new health bar - ALWAYS log this
        // VIS-001-A: Pass ownerId for team-colored background
        console.log(`[CombatVisualizationManager] TICK ${state.tick} - ✓ CREATING HEALTH BAR:`, {
          unitId: unit.unitId.substring(0, 30),
          position: position,
          health: `${unit.health}/${unit.maxHealth}`,
          ownerId: unit.ownerId
        });
        healthBar = this.healthBarRenderer.createHealthBar(unit.health, unit.maxHealth, position, unit.ownerId);
        this.healthBars.set(unit.unitId, healthBar);
        this.effectContainers.healthBars.addChild(healthBar.container);
        console.log(`[CombatVisualizationManager] TICK ${state.tick} - Total health bars now:`, this.healthBars.size);
      } else {
        // Update existing health bar
        this.healthBarRenderer.updateHealthBar(healthBar, unit.health, unit.maxHealth);

        // Update position
        healthBar.container.x = position.x;
        healthBar.container.y = position.y;
      }

      // Hide health bars at full HP — reduces visual noise during spawn walk and for undamaged units
      healthBar.container.visible = (unit.health < unit.maxHealth);

      // L4-BATCH1-007: Update execute range indicator based on health
      this.healthBarEffectsManager.updateExecuteRangeIndicator(
        unit.unitId,
        healthBar,
        unit.health,
        unit.maxHealth
      );
    }

    // Check for dead or removed units (immediate removal)
    const unitsToRemove: string[] = [];

    for (const [unitId] of this.unitsInCombat.entries()) {
      const unit = state.units.find(u => u.unitId === unitId);

      // Remove if unit is dead or removed from state
      if (!unit || unit.status === 'dead') {
        unitsToRemove.push(unitId);
      }
    }

    // Clean up removed units
    for (const unitId of unitsToRemove) {
      if (state.tick <= 5 || state.tick % 60 === 0) {
        console.log(`[CombatVisualizationManager] TICK ${state.tick} - Removing health bar for dead/removed unit:`, unitId.substring(0, 30));
      }
      this.removeHealthBar(unitId);
    }

    // Summary log (periodically)
    if (state.tick <= 5 || state.tick % 60 === 0) {
      console.log(`[CombatVisualizationManager] TICK ${state.tick} - Health bar summary:`, {
        unitsInCombat: this.unitsInCombat.size,
        healthBarsActive: this.healthBars.size
      });
    }
  }

  /**
   * Handle attack_started events
   * Triggers attack animations BEFORE damage is applied
   * Part of the delayed damage system for proper animation synchronization
   * ENHANCED (TASK-ATTR-017): Now also triggers combat attribute effects
   */
  private handleAttackStartedEvents(state: CombatState): void {
    // Filter for attack_started events
    const attackEvents = state.events?.filter(isAttackStartedEvent) || [];

    if (attackEvents.length === 0) return;

    console.log(`[CombatVisualizationManager] TICK ${state.tick} - Processing ${attackEvents.length} attack_started events`);

    for (const attackEvent of attackEvents) {
      const attackerId = attackEvent.data.attackerId;
      const targetId = attackEvent.data.targetId;
      const attackType = attackEvent.data.attackType;
      const abilityId = attackEvent.data.abilityId;

      console.log(`[CombatVisualizationManager] TICK ${state.tick} - Attack started:`, {
        attacker: attackerId.substring(0, 20),
        target: targetId.substring(0, 20),
        attackType,
        abilityId
      });

      // TASK-VIS-004: Log ability usage to combat log
      const attackerUnit = state.units.find(u => u.unitId === attackerId);
      const targetUnit = state.units.find(u => u.unitId === targetId);
      const attackerName = this.getCreatureName(attackerUnit);
      const targetName = this.getCreatureName(targetUnit);
      getCombatLogService().logAbility(
        attackerName,
        abilityId || 'Attack',
        targetName
      );

      // Phase 2: Register attack with correlation service and schedule impact callback
      // For melee attacks, this schedules a callback at the impact frame offset
      if (this.actionCorrelationService.isEnabled()) {
        const correlationKey = this.actionCorrelationService.registerAttackStarted(attackEvent.data, state.tick);

        // If melee attack was registered, schedule the animation impact callback
        if (correlationKey && attackType === 'melee') {
          // Use the configured melee impact offset (default 300ms)
          const meleeImpactOffset = 300; // ms - matches ActionCorrelationConfig.meleeImpactOffset
          this.animationStateMachine.scheduleImpact(
            attackerId,
            attackEvent.data.estimatedImpactTick,
            'melee',
            meleeImpactOffset
          );
        }
      }

      // Calculate facing direction from attacker to target (axial coordinates)
      let attackDirection: number | undefined;
      if (attackerUnit?.position && targetUnit?.position) {
        const dq = targetUnit.position.q - attackerUnit.position.q;
        const dr = targetUnit.position.r - attackerUnit.position.r;
        const degrees = Math.atan2(dr, dq) * (180 / Math.PI);
        attackDirection = degreesToHexDirection(degrees);
      }

      // Trigger attack animation via animation state machine
      // The state machine will transition the attacker to ATTACKING state
      this.animationStateMachine.updateState(attackerId, {
        isAttacking: true,
        isMoving: false,
        health: state.units.find(u => u.unitId === attackerId)?.health || 0,
        direction: attackDirection
      });

      // Wind-up telegraph: visual lean to indicate attack start
      if (attackerUnit?.position && targetUnit?.position) {
        const dq = targetUnit.position.q - attackerUnit.position.q;
        const dr = targetUnit.position.r - attackerUnit.position.r;
        const magnitude = Math.sqrt(dq * dq + dr * dr) || 1;
        const normalizedDq = dq / magnitude;
        const normalizedDr = dr / magnitude;

        if (attackType === 'melee') {
          // Melee: lean forward toward target (4px over 200ms) + white flash
          const leanPixels = 4;
          // Convert axial direction to approximate pixel offset
          // In flat-top hex: dq maps to ~x, dr maps to ~y (roughly)
          this.gridRenderer.applyWindupOffset(
            attackerId,
            normalizedDq * leanPixels,
            normalizedDr * leanPixels,
            200
          );
          this.gridRenderer.flashSprite(attackerId, 0xFFFFFF, 100);
        } else if (attackType === 'ranged') {
          // Ranged: rear back away from target (3px over 150ms)
          const rearPixels = 3;
          this.gridRenderer.applyWindupOffset(
            attackerId,
            -normalizedDq * rearPixels,
            -normalizedDr * rearPixels,
            150
          );
        }
      }

      // L4-COMBAT-ANIM-001: Trigger effects for any attack with abilityId and visual attributes
      // Previously only triggered for attackType === 'ability' which the backend never sends
      // NOTE: Wrapped in try-catch to prevent effect errors from blocking ranged projectile spawn

      // DEBUG: Log effect trigger prerequisites
      console.log(`[CombatVisualizationManager] TICK ${state.tick} - Effect trigger check:`, {
        hasEffectLayer: !!this.effectLayer,
        hasAbilityId: !!abilityId,
        abilityId,
        attackerId: attackerId.substring(0, 30),
        storedUnitIds: Array.from(this.unitVisualAttributes.keys()).map(k => k.substring(0, 30)),
        hasStoredAttrs: this.unitVisualAttributes.has(attackerId)
      });

      if (this.effectLayer && abilityId) {
        try {
          // Get attacker's visual attributes
          const visualAttributes = this.unitVisualAttributes.get(attackerId);

          if (visualAttributes && visualAttributes.length > 0) {
            console.log(`[CombatVisualizationManager] TICK ${state.tick} - Found ${visualAttributes.length} visualAttributes for attacker`);
            // Find matching attribute by abilityId (or use first if no match)
            let attribute = visualAttributes.find(attr => attr.attributeId === abilityId);
            if (!attribute) {
              attribute = visualAttributes[0]; // Fallback to first attribute
              console.log(`[CombatVisualizationManager] TICK ${state.tick} - No attribute matched abilityId ${abilityId}, using first attribute`);
            }

            // Get attacker and target sprite positions
            const attackerSprite = this.gridRenderer.getSpriteByUnitId(attackerId);
            const targetSprite = this.gridRenderer.getSpriteByUnitId(targetId);

            // DEBUG: Log sprite positions for effect placement
            // NOTE: sprite.x/y is relative to container (always 0), container.x/y is world position
            console.log(`[CombatVisualizationManager] TICK ${state.tick} - Sprite positions for effect:`, {
              attackerFound: !!attackerSprite?.container,
              attackerContainerPos: attackerSprite?.container ? { x: attackerSprite.container.x, y: attackerSprite.container.y } : null,
              targetFound: !!targetSprite?.container,
              targetContainerPos: targetSprite?.container ? { x: targetSprite.container.x, y: targetSprite.container.y } : null
            });

            if (attackerSprite?.container && targetSprite?.container) {
              // Use CONTAINER position for effect placement (sprite.x/y is always 0 relative to container)
              // The container is positioned in world space, which matches the effect layer coordinate system

              // Calculate midpoint between attacker and target containers
              const midpoint = {
                x: (attackerSprite.container.x + targetSprite.container.x) / 2,
                y: (attackerSprite.container.y + targetSprite.container.y) / 2
              };

              // Get visual options based on damage type
              const blendMode = getBlendModeForDamageType(attribute.damageType);
              const scale = getScaleForDamageType(attribute.damageType);

              // Trigger effect at midpoint with visual options
              const effectId = this.effectLayer.triggerEffect(
                attribute.attributeId,
                attribute.effectFrames,
                midpoint,
                {
                  blendMode,
                  scale
                }
              );

              console.log(`[CombatVisualizationManager] TICK ${state.tick} - Effect triggered:`, {
                effectId,
                attributeId: attribute.attributeId,
                attributeName: attribute.name,
                damageType: attribute.damageType,
                blendMode,
                scale,
                position: midpoint,
                frameCount: attribute.effectFrames.length
              });

              // TASK-VIS-003: Show ability name popup at attacker position
              this.abilityPopupManager?.showPopup(
                abilityId || 'Attack',
                attackerSprite.container.x,
                attackerSprite.container.y,
                attribute.damageType
              );
            } else {
              console.warn(`[CombatVisualizationManager] TICK ${state.tick} - Cannot trigger effect - missing container:`, {
                attackerId: attackerId.substring(0, 20),
                targetId: targetId.substring(0, 20),
                hasAttackerContainer: !!attackerSprite?.container,
                hasTargetContainer: !!targetSprite?.container
              });
            }
          } else {
            // TASK-EFD-004: Request missing effectFrames from server (fallback mechanism)
            console.warn(`[CombatVisualizationManager] TICK ${state.tick} - No visual attributes for attacker:`, attackerId.substring(0, 20));

            if (!this.pendingEffectFramesRequest && this.currentMatchId && this.socketClient.requestEffectFrames) {
              this.pendingEffectFramesRequest = true;
              console.warn(`[CombatVisualizationManager] Requesting missing effectFrames for match ${this.currentMatchId}`);
              this.socketClient.requestEffectFrames();

              // Reset flag after 1 second to allow retry if needed
              setTimeout(() => {
                this.pendingEffectFramesRequest = false;
              }, 1000);
            }
            // Note: Attack animation continues - effect just won't show this tick
          }
        } catch (effectError) {
          // Log error but don't let it block projectile spawning
          console.error(`[CombatVisualizationManager] TICK ${state.tick} - Effect trigger failed (non-blocking):`, effectError);
        }
      } else {
        // Log why effect was skipped
        if (!this.effectLayer) {
          console.warn(`[CombatVisualizationManager] TICK ${state.tick} - Effect skipped: effectLayer is null`);
        }
        if (!abilityId) {
          console.log(`[CombatVisualizationManager] TICK ${state.tick} - Effect skipped: no abilityId in attack event`);
        }
      }

      // L4-RANGED-004: Spawn projectile for ranged attacks
      // L4-RANGED-DEBUG: Log before ranged check
      console.log(`[CombatVisualizationManager] TICK ${state.tick} - Checking for ranged projectile:`, {
        attackType,
        isRanged: attackType === 'ranged',
        hasEstimatedImpactTick: !!attackEvent.data.estimatedImpactTick,
        estimatedImpactTick: attackEvent.data.estimatedImpactTick
      });

      if (attackType === 'ranged') {
        this.spawnTimedProjectile(
          attackerId,
          targetId,
          abilityId || 'generic',
          attackEvent.tick,
          attackEvent.data.estimatedImpactTick
        );
      }
    }
  }

  /**
   * Spawn a timed projectile for ranged attacks (L4-RANGED-004)
   * Called when attack_started event has attackType: 'ranged'
   */
  private spawnTimedProjectile(
    attackerId: string,
    targetId: string,
    abilityId: string,
    currentTick: number,
    estimatedImpactTick: number
  ): void {
    // L4-RANGED-DEBUG: Log entry to spawnTimedProjectile
    console.log('[CombatVisualizationManager] spawnTimedProjectile called:', {
      attackerId: attackerId.substring(0, 20),
      targetId: targetId.substring(0, 20),
      abilityId,
      currentTick,
      estimatedImpactTick,
      tickDelta: estimatedImpactTick - currentTick
    });

    // Get attacker and target sprite positions
    const attackerSprite = this.gridRenderer.getSpriteByUnitId(attackerId);
    const targetSprite = this.gridRenderer.getSpriteByUnitId(targetId);

    if (!attackerSprite?.container || !targetSprite?.container) {
      console.warn('[CombatVisualizationManager] Cannot spawn projectile - missing sprite:', {
        attackerId: attackerId.substring(0, 20),
        targetId: targetId.substring(0, 20),
        hasAttacker: !!attackerSprite?.container,
        hasTarget: !!targetSprite?.container
      });
      return;
    }

    // Calculate duration from tick delta (server runs at 60 TPS)
    const tickDelta = estimatedImpactTick - currentTick;
    const durationMs = this.projectileRenderer.calculateDurationFromTicks(tickDelta, 60);

    // Get attacker's projectile sprite data (if available)
    const projectileData = this.gridRenderer.getProjectileDataByUnitId(attackerId);

    // Spawn timed projectile with creature's sprite frames if available
    const projectile = this.projectileRenderer.spawnProjectileWithTiming({
      abilityId,
      source: { x: attackerSprite.container.x, y: attackerSprite.container.y },
      target: { x: targetSprite.container.x, y: targetSprite.container.y },
      durationMs,
      spriteFrames: projectileData?.frames,
      element: projectileData?.element,
    });

    // Add to container and tracking
    this.effectContainers.projectiles.addChild(projectile.sprite);
    this.activeTimedProjectiles.set(projectile.projectileId, projectile);

    console.log('[CombatVisualizationManager] Spawned timed projectile:', {
      projectileId: projectile.projectileId,
      type: projectile.type,
      abilityId,
      durationMs,
      tickDelta,
      usesSprite: 'usesSprite' in projectile ? projectile.usesSprite : false,
      hasProjectileData: !!projectileData
    });
  }

  /**
   * Handle projectile_spawned events for multi-projectile volleys (L3-MULTI-PROJECTILE)
   * Called for each projectile in a volley with pattern and index data
   */
  private handleProjectileSpawnedEvents(state: CombatState): void {
    // Filter for projectile_spawned events
    const projectileEvents = state.events?.filter(isProjectileLaunchedEvent) || [];

    if (projectileEvents.length === 0) return;

    console.log(`[CombatVisualizationManager] TICK ${state.tick} - Processing ${projectileEvents.length} projectile_spawned events`);

    for (const projectileEvent of projectileEvents) {
      const {
        attackerId,
        targetId,
        abilityId,
        projectileIndex,
        totalProjectiles,
        pattern,
        element,
        impactTick
      } = projectileEvent.data;

      console.log(`[CombatVisualizationManager] TICK ${state.tick} - Projectile spawned:`, {
        attacker: attackerId.substring(0, 20),
        target: targetId.substring(0, 20),
        abilityId,
        projectileIndex,
        totalProjectiles,
        pattern,
        element
      });

      // Get attacker and target sprite positions
      const attackerSprite = this.gridRenderer.getSpriteByUnitId(attackerId);
      const targetSprite = this.gridRenderer.getSpriteByUnitId(targetId);

      if (!attackerSprite?.container || !targetSprite?.container) {
        console.warn('[CombatVisualizationManager] Cannot spawn projectile - missing sprite:', {
          attackerId: attackerId.substring(0, 20),
          targetId: targetId.substring(0, 20),
          hasAttacker: !!attackerSprite?.container,
          hasTarget: !!targetSprite?.container
        });
        continue;
      }

      // Calculate duration from tick delta (server runs at 60 TPS)
      const tickDelta = impactTick - state.tick;
      const durationMs = this.projectileRenderer.calculateDurationFromTicks(tickDelta, 60);

      // Get attacker's projectile sprite data (if available)
      const projectileData = this.gridRenderer.getProjectileDataByUnitId(attackerId);

      // Spawn timed projectile with volley data for pattern-based offsets
      const projectile = this.projectileRenderer.spawnProjectileWithTiming({
        abilityId,
        source: { x: attackerSprite.container.x, y: attackerSprite.container.y },
        target: { x: targetSprite.container.x, y: targetSprite.container.y },
        durationMs,
        spriteFrames: projectileData?.frames,
        element: element || projectileData?.element,
        volleyData: {
          projectileIndex,
          totalProjectiles,
          pattern
        }
      });

      // Add to container and tracking
      this.effectContainers.projectiles.addChild(projectile.sprite);
      this.activeTimedProjectiles.set(projectile.projectileId, projectile);

      console.log('[CombatVisualizationManager] Spawned volley projectile:', {
        projectileId: projectile.projectileId,
        type: projectile.type,
        abilityId,
        durationMs,
        tickDelta,
        pattern,
        index: projectileIndex,
        total: totalProjectiles,
        usesSprite: 'usesSprite' in projectile ? projectile.usesSprite : false,
        hasProjectileData: !!projectileData
      });
    }
  }

  /**
   * Map damage type to damage number display type
   */
  private mapDamageTypeToDisplay(damageType: string): DamageType {
    switch (damageType) {
      case 'magical':
        return DamageType.MAGICAL;
      case 'physical':
      default:
        return DamageType.PHYSICAL;
    }
  }

  /**
   * Update impact particles (L4-JUICE-007)
   * Called every frame to animate particles and remove completed ones
   */
  private updateImpactParticles(): void {
    const currentTime = Date.now();

    // Update all active particles
    const completed = this.impactParticlePool.update(currentTime);

    // Remove completed particles
    for (const particle of completed) {
      this.effectContainers.damageNumbers.removeChild(particle.container);
      this.impactParticlePool.release(particle);
    }
  }

  /**
   * Manage projectiles for ranged attacks (TASK-VIZ-021)
   */
  private manageProjectiles(state: CombatState, events: any): void {
    // Get current projectile IDs from state
    const currentProjectileIds = new Set(state.projectiles.map(p => p.projectileId));

    // Remove projectiles that are no longer in state
    for (const [projectileId, visualProjectile] of this.activeProjectiles.entries()) {
      if (!currentProjectileIds.has(projectileId)) {
        // Remove from container
        this.effectContainers.projectiles.removeChild(visualProjectile.sprite);

        // Destroy sprite
        this.projectileRenderer.destroyProjectile(visualProjectile);

        // Remove from tracking
        this.activeProjectiles.delete(projectileId);
      }
    }

    // Create or update projectiles
    for (const projectile of state.projectiles) {
      let visualProjectile = this.activeProjectiles.get(projectile.projectileId);

      if (!visualProjectile) {
        // Create new projectile
        const sourcePixels = this.hexToPixel(projectile.currentPosition);
        const targetPixels = this.hexToPixel(projectile.targetPosition);

        visualProjectile = this.projectileRenderer.createVisualProjectile(
          projectile.projectileId,
          sourcePixels,
          targetPixels,
          projectile.effectOnImpact || 'arrow.png'
        );

        // Add to container and tracking
        this.effectContainers.projectiles.addChild(visualProjectile.sprite);
        this.activeProjectiles.set(projectile.projectileId, visualProjectile);
      }

      // Update position
      const currentPixels = this.hexToPixel(projectile.currentPosition);
      visualProjectile.sprite.x = currentPixels.x;
      visualProjectile.sprite.y = currentPixels.y;

      // Update rotation to face target
      const targetPixels = this.hexToPixel(projectile.targetPosition);
      const dx = targetPixels.x - currentPixels.x;
      const dy = targetPixels.y - currentPixels.y;
      visualProjectile.sprite.rotation = Math.atan2(dy, dx);
    }
  }

  /**
   * Convert hex coordinates to pixel coordinates (direct calculation)
   * FIX: Use direct hex-to-pixel conversion instead of sprite lookup
   */
  private hexToPixel(hexCoord: { q: number; r: number }): { x: number; y: number } {
    return this.hexToPixelDirect(hexCoord);
  }

  /**
   * Convert hex coordinates to pixel coordinates (direct calculation, supports off-grid)
   * This method can handle deployment positions that are outside the combat grid
   *
   * FIX: Use gridRenderer.hexToPixel() to ensure consistent coordinate transformation
   * with the grid rendering. Previously calculated offset independently using canvas.width
   * which returns device pixel size (2x on retina) instead of logical CSS size.
   */
  private hexToPixelDirect(hexCoord: { q: number; r: number }): { x: number; y: number } {
    // Use the grid renderer's hexToPixel which has the correct cached offset
    return this.gridRenderer.hexToPixel(hexCoord);
  }

  /**
   * Get unit by ID from current state with pixel coordinates
   * Helper for ability event handlers (L2-CLAUDE-ATTRS)
   * @deprecated Use getUnitVisualPosition() or getUnitHexCenter() instead
   */
  private getUnitById(unitId: string): { x: number; y: number } | null {
    if (!this.currentState) {
      return null;
    }

    const unit = this.currentState.units.find(u => u.unitId === unitId);
    if (!unit) {
      return null;
    }

    // Convert hex position to pixel coordinates
    const pixel = this.hexToPixel(unit.position);
    return { x: pixel.x, y: pixel.y };
  }

  /**
   * L3-VIS-SUBHEX-001: Get unit visual position (includes sub-hex offset)
   * Use for: projectiles, beams, damage numbers - anything targeting the sprite
   * Returns the container position which includes the applied sub-hex offset
   */
  private getUnitVisualPosition(unitId: string): { x: number; y: number } | null {
    const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
    if (!spriteData?.container) {
      return null;
    }
    return { x: spriteData.container.x, y: spriteData.container.y };
  }

  /**
   * L3-VIS-SUBHEX-001: Get unit hex center position (no sub-hex offset)
   * Use for: AOE markers, ground effects - anything targeting the hex itself
   * Returns the pure hex center without visual offset
   */
  private getUnitHexCenter(unitId: string): { x: number; y: number } | null {
    if (!this.currentState) {
      return null;
    }

    const unit = this.currentState.units.find(u => u.unitId === unitId);
    if (!unit) {
      return null;
    }

    // Convert hex position to pixel coordinates (no offset)
    const pixel = this.hexToPixel(unit.position);
    return { x: pixel.x, y: pixel.y };
  }

  /**
   * L3-VIS-SUBHEX-001: Calculate sub-hex offset for a unit
   * WC2-style: Sprites stay strictly centered on hex for maximum battle clarity.
   * No sub-hex variance - each unit occupies exactly one hex visually.
   */
  private calculateSubHexOffsetForUnit(_unitId: string, _hex: AxialCoordinate): SubHexOffset {
    // WC2-style: strict hex center, no sub-hex variance
    // This ensures sprites never visually overlap, making battles readable
    return { x: 0, y: 0 };
  }

  /**
   * L3-VIS-SUBHEX-001: Batch update sub-hex offsets for all units
   *
   * Uses 3-phase approach to prevent feedback loops:
   * 1. Detect which units need offset recalculation (hex changed)
   * 2. Calculate all new offsets using current positions
   * 3. Apply all offsets simultaneously
   *
   * Called from renderFrame() before updateSpriteTransforms()
   */
  private updateSubHexOffsets(): void {
    if (!this.currentInterpolatedState?.units) return;

    // Phase 1: Detect units needing updates (those that changed hex)
    const unitsNeedingUpdate: Array<{ unitId: string; destHex: AxialCoordinate }> = [];

    for (const unit of this.currentInterpolatedState.units) {
      if (unit.status === 'dead') continue;

      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);
      if (!spriteData) continue;

      // Round interpolated position to destination hex
      const destHex = { q: Math.round(unit.position.q), r: Math.round(unit.position.r) };

      // Check if hex changed since last update
      if (!spriteData.lastHex ||
          spriteData.lastHex.q !== destHex.q ||
          spriteData.lastHex.r !== destHex.r) {
        unitsNeedingUpdate.push({ unitId: unit.unitId, destHex });
      }
    }

    // Skip if no units need updates
    if (unitsNeedingUpdate.length === 0) return;

    // Phase 2: Calculate all offsets (using current positions before any changes)
    const newOffsets = new Map<string, { offset: SubHexOffset; destHex: AxialCoordinate }>();

    for (const { unitId, destHex } of unitsNeedingUpdate) {
      const offset = this.calculateSubHexOffsetForUnit(unitId, destHex);
      newOffsets.set(unitId, { offset, destHex });
    }

    // Phase 3: Apply all offsets simultaneously (prevents feedback loops)
    for (const [unitId, { offset, destHex }] of newOffsets) {
      const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
      if (spriteData) {
        spriteData.subHexOffset = offset;
        spriteData.lastHex = destHex;
      }
    }
  }

  /**
   * VIS-006-A: Apply visual knockback to defender on hit
   * Pushes the victim sprite away from the attacker for impact feedback
   */
  private applyKnockback(victimId: string, attackerId: string, isCritical: boolean = false): void {
    const victimSprite = this.gridRenderer.getSpriteByUnitId(victimId);
    const attackerSprite = this.gridRenderer.getSpriteByUnitId(attackerId);

    if (!victimSprite?.container || !attackerSprite?.container) {
      return;
    }

    // Calculate direction away from attacker
    const dx = victimSprite.container.x - attackerSprite.container.x;
    const dy = victimSprite.container.y - attackerSprite.container.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;

    // Knockback amount: 6px for critical, 4px for normal
    const knockbackAmount = isCritical ? 6 : 4;
    const knockbackX = (dx / dist) * knockbackAmount;
    const knockbackY = (dy / dist) * knockbackAmount;

    // Apply instant knockback offset
    victimSprite.container.x += knockbackX;
    victimSprite.container.y += knockbackY;

    // Tween back to original position over 150ms
    this.tweenBack(victimSprite.container, knockbackX, knockbackY, 150);
  }

  /**
   * VIS-006-B: Apply attack lunge to attacker on strike
   * Moves the attacker slightly toward the target for impact feedback
   */
  private applyAttackLunge(attackerId: string, targetId: string): void {
    const attackerSprite = this.gridRenderer.getSpriteByUnitId(attackerId);
    const targetSprite = this.gridRenderer.getSpriteByUnitId(targetId);

    if (!attackerSprite?.container || !targetSprite?.container) {
      return;
    }

    // Calculate direction toward target
    const dx = targetSprite.container.x - attackerSprite.container.x;
    const dy = targetSprite.container.y - attackerSprite.container.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;

    // Lunge amount: 3px (subtle but visible)
    const lungeAmount = 3;
    const lungeX = (dx / dist) * lungeAmount;
    const lungeY = (dy / dist) * lungeAmount;

    // Apply instant lunge offset
    attackerSprite.container.x += lungeX;
    attackerSprite.container.y += lungeY;

    // Tween back to original position over 100ms (faster than knockback)
    this.tweenBack(attackerSprite.container, lungeX, lungeY, 100);
  }

  /**
   * VIS-006: Helper to smoothly return sprite to original position
   * Uses requestAnimationFrame for smooth animation
   */
  private tweenBack(container: PIXI.Container, offsetX: number, offsetY: number, duration: number): void {
    const startTime = performance.now();
    const startX = container.x;
    const startY = container.y;
    const targetX = startX - offsetX;
    const targetY = startY - offsetY;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1.0);

      // Ease out quad for natural deceleration
      const easeProgress = 1 - (1 - progress) * (1 - progress);

      container.x = startX + (targetX - startX) * easeProgress;
      container.y = startY + (targetY - startY) * easeProgress;

      if (progress < 1.0) {
        requestAnimationFrame(animate);
      } else {
        // Ensure exact final position
        container.x = targetX;
        container.y = targetY;
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Manage buff icons for units (TASK-VIZ-022)
   */
  private manageBuffIcons(state: CombatState, events: any): void {
    // Collect all current buff/debuff IDs from state
    const currentBuffIds = new Set<string>();
    const buffsByUnit = new Map<string, Array<{ id: string; name: string; duration: number; isBuff: boolean }>>();

    for (const unit of state.units) {
      const unitBuffs: Array<{ id: string; name: string; duration: number; isBuff: boolean }> = [];

      // Add buffs
      for (const buff of unit.activeBuffs || []) {
        currentBuffIds.add(buff.buffId);
        unitBuffs.push({
          id: buff.buffId,
          name: buff.name,
          duration: buff.durationRemaining,
          isBuff: true
        });
      }

      // Add debuffs
      for (const debuff of unit.activeDebuffs || []) {
        currentBuffIds.add(debuff.debuffId);
        unitBuffs.push({
          id: debuff.debuffId,
          name: debuff.name,
          duration: debuff.durationRemaining,
          isBuff: false
        });
      }

      if (unitBuffs.length > 0) {
        buffsByUnit.set(unit.unitId, unitBuffs);
      }
    }

    // Remove buff icons that are no longer in state
    for (const [buffId, buffIcon] of this.activeBuffIcons.entries()) {
      if (!currentBuffIds.has(buffId)) {
        // Remove from container
        this.effectContainers.buffIcons.removeChild(buffIcon.container);

        // Destroy icon
        this.buffIconRenderer.destroyBuffIcon(buffIcon);

        // Remove from tracking
        this.activeBuffIcons.delete(buffId);
      }
    }

    // Create or update buff icons for each unit
    for (const unit of state.units) {
      const unitBuffs = buffsByUnit.get(unit.unitId);
      if (!unitBuffs) continue;

      // Get sprite by unitId (FIX: sprites are keyed by unitId, not hex hash)
      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);
      if (!spriteData?.sprite) continue;

      const spriteX = spriteData.sprite.x;
      const spriteY = spriteData.sprite.y;

      // VIS-004-B: Separate buffs and debuffs for position differentiation
      // Buffs go on the left, debuffs go on the right
      const buffs: typeof unitBuffs = [];
      const debuffs: typeof unitBuffs = [];

      for (const buffData of unitBuffs) {
        const buffType = this.mapBuffNameToType(buffData.name);
        const props = this.buffIconRenderer.getBuffProperties(buffType);
        if (props.isDebuff) {
          debuffs.push(buffData);
        } else {
          buffs.push(buffData);
        }
      }

      // Create or update buff icons (left side, stacking leftward)
      for (let i = 0; i < buffs.length; i++) {
        const buffData = buffs[i];
        const iconX = spriteX - 15 - (i * this.BUFF_ICON_SPACING); // Left of center, stacking left
        const iconY = spriteY - this.BUFF_ICON_OFFSET_Y;

        let buffIcon = this.activeBuffIcons.get(buffData.id);

        if (!buffIcon) {
          const buffType = this.mapBuffNameToType(buffData.name);
          buffIcon = this.buffIconRenderer.createBuffIcon(
            buffType,
            { x: iconX, y: iconY },
            { duration: buffData.duration }
          );
          this.effectContainers.buffIcons.addChild(buffIcon.container);
          this.activeBuffIcons.set(buffData.id, buffIcon);
        } else {
          buffIcon.container.x = iconX;
          buffIcon.container.y = iconY;
          this.buffIconRenderer.updateBuffIcon(buffIcon, {
            duration: buffData.duration,
            position: { x: iconX, y: iconY }
          });
        }
      }

      // Create or update debuff icons (right side, stacking rightward)
      for (let i = 0; i < debuffs.length; i++) {
        const buffData = debuffs[i];
        const iconX = spriteX + 15 + (i * this.BUFF_ICON_SPACING); // Right of center, stacking right
        const iconY = spriteY - this.BUFF_ICON_OFFSET_Y;

        let buffIcon = this.activeBuffIcons.get(buffData.id);

        if (!buffIcon) {
          const buffType = this.mapBuffNameToType(buffData.name);
          buffIcon = this.buffIconRenderer.createBuffIcon(
            buffType,
            { x: iconX, y: iconY },
            { duration: buffData.duration }
          );
          this.effectContainers.buffIcons.addChild(buffIcon.container);
          this.activeBuffIcons.set(buffData.id, buffIcon);
        } else {
          buffIcon.container.x = iconX;
          buffIcon.container.y = iconY;
          this.buffIconRenderer.updateBuffIcon(buffIcon, {
            duration: buffData.duration,
            position: { x: iconX, y: iconY }
          });
        }
      }
    }
  }

  /**
   * Map buff/debuff name to BuffType enum
   */
  private mapBuffNameToType(name: string): BuffType {
    const lowerName = name.toLowerCase();

    if (lowerName.includes('poison')) return BuffType.POISON;
    if (lowerName.includes('burn')) return BuffType.BURN;
    if (lowerName.includes('regen')) return BuffType.REGENERATION;
    if (lowerName.includes('shield')) return BuffType.SHIELD;
    if (lowerName.includes('stun')) return BuffType.STUN;
    if (lowerName.includes('haste') || lowerName.includes('speed')) return BuffType.HASTE;
    if (lowerName.includes('slow')) return BuffType.SLOW;

    // Default based on whether it's typically a buff or debuff
    return BuffType.SHIELD; // Safe default
  }

  /**
   * Check for health bar timeouts (called periodically)
   */
  private checkHealthBarTimeouts(): void {
    if (!this.currentState) {
      return;
    }

    const currentTime = Date.now();
    const unitsToRemove: string[] = [];

    for (const [unitId, lastCombatTime] of this.unitsInCombat.entries()) {
      const unit = this.currentState.units.find(u => u.unitId === unitId);

      // Skip if unit doesn't exist
      if (!unit) {
        continue;
      }

      // Remove if timeout expired and unit has no target
      const timeSinceLastCombat = currentTime - lastCombatTime;
      if (timeSinceLastCombat >= this.COMBAT_TIMEOUT && !unit.currentTarget) {
        unitsToRemove.push(unitId);
      }
    }

    // Clean up timed-out units
    for (const unitId of unitsToRemove) {
      this.removeHealthBar(unitId);
    }
  }

  /**
   * Remove health bar for a unit
   */
  private removeHealthBar(unitId: string): void {
    const healthBar = this.healthBars.get(unitId);

    if (healthBar) {
      // Cancel any active flash effects before destroying health bar (CRITICAL for cleanup)
      this.healthBarEffectsManager.cancelFlash(unitId);

      // L4-BATCH1-007: Cancel execute range effect before destroying health bar
      this.healthBarEffectsManager.cancelExecuteRangeEffect(unitId);

      this.healthBarRenderer.destroyHealthBar(healthBar);
      this.healthBars.delete(unitId);
    }

    this.unitsInCombat.delete(unitId);
  }

  /**
   * Start the 60 FPS render loop (TASK-RENDER-001)
   */
  private startRenderLoop(): void {
    console.log('[CombatVisualizationManager] startRenderLoop() called - renderLoopId:', this.renderLoopId);

    if (this.renderLoopId !== null) {
      console.warn('[CombatVisualizationManager] Render loop already running, renderLoopId:', this.renderLoopId);
      return;
    }

    this.lastFrameTime = 0;
    console.log('[CombatVisualizationManager] Calling requestAnimationFrame...');
    this.renderLoopId = requestAnimationFrame(this.renderLoop);
    console.log('[CombatVisualizationManager] Render loop started with ID:', this.renderLoopId);
  }

  /**
   * Stop the render loop (TASK-RENDER-001)
   */
  private stopRenderLoop(): void {
    if (this.renderLoopId !== null) {
      cancelAnimationFrame(this.renderLoopId);
      this.renderLoopId = null;
      console.log('[CombatVisualizationManager] Render loop stopped');
    }
  }

  /**
   * Main render loop - called at 60 FPS (TASK-RENDER-001, TASK-RENDER-002)
   */
  private renderLoop = (currentTime: number): void => {
    // Early exit if destroyed to prevent any further rendering
    if (this.isDestroyed || !this.isRunning) {
      console.log('[CombatVisualizationManager] Render loop exiting - isDestroyed:', this.isDestroyed, 'isRunning:', this.isRunning);
      return;
    }

    console.log('[CombatVisualizationManager] DEBUG: renderLoop CALLED at', currentTime, 'lastFrameTime:', this.lastFrameTime);
    // DEBUG: Log first frame only
    if (this.lastFrameTime === 0) {
      console.log('[CombatVisualizationManager] First render loop frame at', currentTime);
    }

    // Calculate delta time (TASK-RENDER-002)
    let deltaTime = 0;
    if (this.lastFrameTime !== 0) {
      deltaTime = (currentTime - this.lastFrameTime) / 1000; // Convert to seconds
      deltaTime = Math.min(deltaTime, this.MAX_DELTA_TIME); // Clamp to max (prevents huge jumps from tab backgrounding)
    }
    this.lastFrameTime = currentTime;

    // Render the current frame
    this.renderFrame(deltaTime);

    // Schedule next frame only if still running and not destroyed
    if (this.isRunning && !this.isDestroyed) {
      this.renderLoopId = requestAnimationFrame(this.renderLoop);
    } else {
      console.log('[CombatVisualizationManager] Render loop stopped - isRunning:', this.isRunning, 'isDestroyed:', this.isDestroyed);
    }
  };

  /**
   * Render a single frame - called 60 times per second (TASK-RENDER-001)
   * This is where continuous visual updates happen between state ticks
   */
  private renderFrame(deltaTime: number): void {
    // Early exit if destroyed or app is no longer valid
    if (this.isDestroyed || !this.app || (this.app as any).destroyed) {
      return;
    }

    try {
      // TASK-SMOOTH-003: Get interpolated state (100ms in the past for smooth rendering)
      const renderTime = performance.now() - this.INTERPOLATION_DELAY_MS;
      this.currentInterpolatedState = this.getInterpolatedState(renderTime);

      // DEBUG: Log when interpolation starts working (first 5 times only)
      if (this.currentInterpolatedState && this.renderCallCount <= 5) {
        console.log(`[CombatVisualizationManager] Interpolation active - frame ${this.renderCallCount}, buffer size: ${this.stateBuffer.length}`);
      }

      // TASK-SMOOTH-INTEGRATION: Use interpolated state for smooth unit positions at 60 FPS
      // The interpolated state is used by updateSpriteTransforms() to smoothly render units

      // Release buffered events at steady tick cadence
      const bufferedEvents = this.jitterBuffer.dequeue(deltaTime * 1000);
      this.processBufferedEvents(bufferedEvents);

      // Update animation states at 60 FPS (TASK-TIMING-002)
      this.updateAnimationStates();

      // L3-VIS-SUBHEX-001: Update sub-hex offsets for units that changed hex
      this.updateSubHexOffsets();

      // Update sprite transforms (TASK-RENDER-005)
      this.updateSpriteTransforms();

      // Update health bar positions (TASK-RENDER-008)
      this.updateHealthBarPositions();

      // Update health bar flash effects
      const currentTime = Date.now();
      this.healthBarEffectsManager.update(currentTime);

      // L4-BATCH1-007: Update execute range pulsing effects
      this.healthBarEffectsManager.updateExecuteRangeEffects(currentTime);

      // Update damage numbers (TASK-RENDER-011)
      this.updateDamageNumbers(deltaTime);

      // Phase 2: Update scheduled animation impact callbacks
      // This fires impact events that notify the correlation service
      this.animationStateMachine.updateImpacts();

      // Phase 2: Update action correlation and display ready damages
      // Checks for impacts that have been reached and releases correlated damage
      this.updateCorrelatedDamages(deltaTime);

      // TASK-DEATH-004: Update death sequences and smoke effects
      this.updateDeathSequences(deltaTime);

      // Update impact particles (L4-JUICE-007)
      this.updateImpactParticles();

      // Update projectile positions (TASK-RENDER-014)
      this.updateProjectiles(deltaTime);

      // L4-RANGED-004: Update timed projectiles (event-driven)
      this.updateTimedProjectiles();

      // TASK-ATTR-015: Update effect layer
      this.effectLayer?.update(deltaTime);

      // TASK-VIS-003: Update ability popups
      this.abilityPopupManager?.update();

      // L2-CLAUDE-ATTRS: Update cooldown indicators
      this.updateCooldownIndicators();

      // TASK-BUGFIX-RENDER-003: Explicitly render the canvas
      // This was the missing piece causing the black screen issue

      // DEBUG: Log render calls (first 10 frames only)
      if (!this.renderCallCount) this.renderCallCount = 0;
      this.renderCallCount++;
      if (this.renderCallCount <= 10) {
        console.log(`[CombatVisualizationManager] CALLING app.render() - frame ${this.renderCallCount}`);
      }

      this.app.render();

      if (this.renderCallCount <= 10) {
        console.log(`[CombatVisualizationManager] app.render() COMPLETED - frame ${this.renderCallCount}`);
      }
    } catch (error) {
      console.error('[CombatVisualizationManager] Error in renderFrame:', error);
    }
  }

  /**
   * Update sprite transforms at 60 FPS (TASK-RENDER-005, TASK-SMOOTH-INTEGRATION)
   *
   * Uses interpolated state from state buffer to smoothly render units at 60 FPS
   * between server state updates (10 Hz → 60 FPS smooth rendering).
   */
  private updateSpriteTransforms(): void {
    // IMPORTANT: Only update positions when we have a TRUE interpolated state
    // If we fall back to currentState, sprites will keep their initial positions
    // from renderCreatureWithId() until we have enough buffer data
    if (!this.currentInterpolatedState || !this.currentInterpolatedState.units) {
      return;
    }

    // Update transform for each unit using interpolated positions
    for (const unit of this.currentInterpolatedState.units) {
      // Skip dead units - they use exact positions for death animation
      if (unit.status === 'dead') {
        continue;
      }

      // Get sprite by unitId
      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);

      // Skip if sprite doesn't exist yet (may not be rendered yet)
      if (!spriteData || !spriteData.container) {
        continue;
      }

      // TASK-SMOOTH-INTEGRATION: Convert interpolated hex position to screen pixels
      // This provides smooth 60 FPS movement between server state updates (10 Hz)
      const pixel = this.gridRenderer.hexToPixel(unit.position);

      // L3-VIS-SUBHEX-001: Apply sub-hex offset for WC3-style visual separation
      const offset = spriteData.subHexOffset || { x: 0, y: 0 };

      // CRITICAL FIX: Update CONTAINER position, not sprite position
      // The sprite sits at (0,0) relative to its container
      // The container holds the actual screen position + sub-hex offset
      spriteData.container.x = pixel.x + offset.x;
      spriteData.container.y = pixel.y + offset.y;
    }
  }

  /**
   * Update health bar positions to track sprite positions (TASK-RENDER-008)
   *
   * Called every frame (60 FPS) to ensure health bars follow their unit sprites smoothly.
   */
  private updateHealthBarPositions(): void {
    if (!this.currentState || !this.currentState.units) {
      return;
    }

    // Iterate through all active health bars
    for (const [unitId, healthBar] of this.healthBars.entries()) {
      // Find the unit in current state
      const unit = this.currentState.units.find(u => u.unitId === unitId);

      if (!unit) {
        // Unit not in current state (may have been removed), skip update
        continue;
      }

      // Get sprite by unitId (FIX: sprites are keyed by unitId, not hex hash)
      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);

      if (!spriteData || !spriteData.container) {
        // Sprite not found (may not be rendered yet), skip update
        continue;
      }

      // Update health bar position to track sprite CONTAINER (not sprite itself)
      // The sprite sits at (0,0) relative to its container
      // The container holds the actual screen position
      healthBar.container.x = spriteData.container.x;
      healthBar.container.y = spriteData.container.y - this.HEALTH_BAR_OFFSET_Y;
    }
  }

  /**
   * Update animation states at 60 FPS (TASK-TIMING-002)
   *
   * Checks current game state and updates animation state machine.
   * Called every frame to ensure smooth animation transitions.
   */
  private updateAnimationStates(): void {
    if (!this.currentState) {
      return;
    }

    // DEBUG: Log all units' movementPath status at tick 100, 200, 300
    if (this.currentState.tick === 100 || this.currentState.tick === 200 || this.currentState.tick === 300) {
      console.log(`[CombatVisualizationManager] TICK ${this.currentState.tick} - Movement Path Status:`,
        this.currentState.units.map(u => ({
          unitId: u.unitId.substring(0, 30),
          hasMovementPath: !!u.movementPath,
          pathLength: u.movementPath?.length || 0,
          position: u.position,
          currentTarget: u.currentTarget?.substring(0, 30) || 'none'
        }))
      );
    }

    // Update animation states for all units based on current state
    for (const unit of this.currentState.units) {
      // Don't override one-shot animations (ATTACK, CAST) that are still playing.
      // These are triggered by attack_started events and should complete naturally
      // via onAnimationComplete, not be interrupted by polling-based state updates.
      const currentAnimState = this.animationStateMachine.getState(unit.unitId);
      if (currentAnimState === AnimationState.ATTACK || currentAnimState === AnimationState.CAST) {
        continue;
      }

      // Determine if unit is moving (has a movement path or is in spawn movement)
      const isMoving = Boolean(unit.movementPath && unit.movementPath.length > 0) ||
                       Boolean(unit.spawnTicksRemaining && unit.spawnTicksRemaining > 0);

      // Determine if unit is attacking (has target and is in range)
      // FIX: Only trigger attack animations if socket is connected
      // This prevents the infinite attack loop when socket disconnects
      const isAttacking = this.isSocketConnected &&
        Boolean(unit.currentTarget && (!isMoving || unit.attackCooldownRemaining === 0));

      // Update animation state machine (detailed logging now in state machine)
      this.animationStateMachine.updateState(unit.unitId, {
        isMoving: this.isSocketConnected ? isMoving : false, // Stop movement on disconnect
        isAttacking,
        health: unit.health
      });
    }
  }

  /**
   * Update death sequences and smoke effects (TASK-DEATH-004)
   *
   * Called every frame to advance death animations and update smoke particle effects.
   * Handles the death sequence phases: DEATH_ANIMATION -> SMOKE_EFFECT -> COMPLETE
   */
  private updateDeathSequences(deltaTime: number): void {
    // Convert delta time from seconds to milliseconds
    const deltaTimeMs = deltaTime * 1000;

    // Update death sequence manager (handles phase transitions)
    this.deathSequenceManager.update(deltaTimeMs);

    // Update smoke effect pool (handles particle animations)
    if (this.smokeEffectPool) {
      this.smokeEffectPool.update(deltaTimeMs);
    }

    // L3-LINGERING-HAZARDS: Update hazard zone animations
    if (this.hazardZoneRenderer) {
      this.hazardZoneRenderer.update();
    }

    // Update sprite alpha for units in smoke phase (gradual fade)
    for (const unitId of this.dyingUnits) {
      const phase = this.deathSequenceManager.getPhase(unitId);
      const progress = this.deathSequenceManager.getProgress(unitId);

      if (phase === DeathPhase.SMOKE_EFFECT && progress !== null) {
        const spriteData = this.gridRenderer.getSpriteByUnitId(unitId);
        if (spriteData?.container) {
          // Fade from 0.5 to 0 during smoke phase
          spriteData.container.alpha = 0.5 * (1 - progress);
        }
      }
    }
  }

  /**
   * Update projectile positions with interpolation (TASK-RENDER-014)
   *
   * Interpolates projectile positions between server state updates for smooth 60 FPS movement.
   * This runs every frame to ensure projectiles move smoothly even though the server only
   * updates at 10 Hz.
   */
  private updateProjectiles(_deltaTime: number): void {
    if (!this.currentState || !this.activeProjectiles.size) {
      return;
    }

    // Calculate interpolation factor based on time since last state update
    let interpolationFactor = 0.0;

    if (this.previousState && this.lastStateUpdateTime > 0) {
      const currentTime = Date.now();
      const timeSinceUpdate = currentTime - this.lastStateUpdateTime;

      // Calculate factor: 0.0 at state arrival, approaches 1.0 over time
      interpolationFactor = Math.min(timeSinceUpdate / this.STATE_UPDATE_INTERVAL, 1.0);
    }

    // Update each active projectile
    for (const [projectileId, visualProjectile] of this.activeProjectiles.entries()) {
      // Find the projectile in current state
      const currentProjectile = this.currentState.projectiles.find(p => p.projectileId === projectileId);

      if (!currentProjectile) {
        // Projectile not in current state (may have been removed), skip update
        continue;
      }

      // Get current server position
      const currentPixels = this.hexToPixel(currentProjectile.currentPosition);

      // If we have a previous state, interpolate between previous and current position
      if (this.previousState && interpolationFactor > 0) {
        const previousProjectile = this.previousState.projectiles.find(p => p.projectileId === projectileId);

        if (previousProjectile) {
          // Get previous position
          const previousPixels = this.hexToPixel(previousProjectile.currentPosition);

          // Linear interpolation between previous and current position
          visualProjectile.sprite.x = previousPixels.x + (currentPixels.x - previousPixels.x) * interpolationFactor;
          visualProjectile.sprite.y = previousPixels.y + (currentPixels.y - previousPixels.y) * interpolationFactor;

          // Update rotation to face direction of travel
          const dx = currentPixels.x - previousPixels.x;
          const dy = currentPixels.y - previousPixels.y;

          if (dx !== 0 || dy !== 0) {
            visualProjectile.sprite.rotation = Math.atan2(dy, dx);
          }
        } else {
          // Projectile didn't exist in previous state (newly spawned), use current position
          visualProjectile.sprite.x = currentPixels.x;
          visualProjectile.sprite.y = currentPixels.y;
        }
      } else {
        // No previous state or no interpolation needed, use current position
        visualProjectile.sprite.x = currentPixels.x;
        visualProjectile.sprite.y = currentPixels.y;
      }
    }
  }

  /**
   * Update timed projectiles at 60 FPS (L4-RANGED-004)
   * Animates event-driven projectiles independently of server state
   */
  private updateTimedProjectiles(): void {
    if (this.activeTimedProjectiles.size === 0) {
      return;
    }

    const currentTime = performance.now();
    const completedProjectiles: string[] = [];

    for (const [projectileId, projectile] of this.activeTimedProjectiles.entries()) {
      // Update projectile animation
      this.projectileRenderer.updateTimedProjectile(projectile, currentTime);

      // Check if completed
      if (projectile.isComplete) {
        completedProjectiles.push(projectileId);
      }
    }

    // Remove completed projectiles
    for (const projectileId of completedProjectiles) {
      const projectile = this.activeTimedProjectiles.get(projectileId);
      if (projectile) {
        // Remove from container
        this.effectContainers.projectiles.removeChild(projectile.sprite);

        // Destroy sprite
        this.projectileRenderer.destroyProjectile(projectile);

        // Remove from tracking
        this.activeTimedProjectiles.delete(projectileId);

        console.log('[CombatVisualizationManager] Timed projectile completed:', projectileId);
      }
    }
  }

  /**
   * Get active timed projectiles (L4-RANGED-004)
   * For testing and debugging event-driven projectiles
   */
  public getActiveTimedProjectiles(): SpriteTimedProjectile[] {
    return Array.from(this.activeTimedProjectiles.values());
  }

  /**
   * Update cooldown indicators for all units with dynamic abilities (L2-CLAUDE-ATTRS)
   *
   * Extracts cooldown state from combat state and updates visual indicators
   * showing ability cooldown progress above each unit.
   */
  private updateCooldownIndicators(): void {
    if (!this.cooldownIndicatorRenderer || !this.currentState) {
      return;
    }

    // Update indicators for each unit with dynamic abilities
    for (const unit of this.currentState.units) {
      // Skip dead units
      if (unit.status === 'dead') {
        continue;
      }

      // Skip units without dynamic abilities
      if (!unit.dynamicAbilities || unit.dynamicAbilities.length === 0) {
        continue;
      }

      // Get sprite position for indicator placement
      const spriteData = this.gridRenderer.getSpriteByUnitId(unit.unitId);
      if (!spriteData || !spriteData.container) {
        continue;
      }

      // Extract cooldown state for each dynamic ability
      const abilityCooldowns: AbilityCooldownState[] = unit.dynamicAbilities.map((ability: any) => ({
        abilityId: ability.id,
        abilityName: ability.name,
        element: this.getAbilityElement(ability),
        cooldownRemaining: ability.cooldownRemaining || 0,
        cooldownTotal: ability.cooldownTicks || 1
      }));

      // Update cooldown indicators at sprite position
      this.cooldownIndicatorRenderer.updateUnitCooldowns(
        unit.unitId,
        spriteData.container.x,
        spriteData.container.y,
        abilityCooldowns
      );
    }
  }

  /**
   * Get element type for an ability (for cooldown indicator color/icon)
   * L4-ABILITY-ICONS-003: Fixed to check ability.element first, which is the
   * correct field where DynamicAbility stores its element type.
   */
  private getAbilityElement(ability: any): string {
    // Primary: Check ability.element (where DynamicAbility stores element type)
    if (ability.element && typeof ability.element === 'string') {
      return ability.element;
    }

    // Fallback: Check effects[0].damageType (legacy/alternative structure)
    if (ability.effects && ability.effects.length > 0) {
      const firstEffect = ability.effects[0];
      if (firstEffect.damageType) {
        return firstEffect.damageType;
      }
    }

    // Default to physical
    return 'physical';
  }

  // REMOVED: handleAnimationTransitions(), hasUnitsWithDeploymentPositions(), and createSyntheticSpawnState()
  // These were part of the over-engineered animation system and spawn-to-battle synthetic state creation
  // that was causing the rendering regression. Rendering is now simple and state-driven.
}
